/**
 * HubSpot CRM Integration Adapter (Story 25.1, AC #5, #6, #7)
 *
 * Plain object implementing IntegrationAdapter (ADR-013).
 * Focuses on CRM v3: contacts, deals, companies, pipelines.
 *
 * Rate limits: 110 req/10s (OAuth apps per account) = ~660 req/min.
 * Access token expires in 30 minutes (1800s) — requires aggressive proactive refresh.
 *
 * _reportType records:
 *   - "summary": totals + derived rates for the full date range
 *   - "dailyOverview": one record per day with daily counts
 *   - "dealsByStage": one record per pipeline stage for funnel/bar chart widgets
 *   - "contactsBySource": one record per hs_analytics_source value (Story 25.2)
 *   - "contactsByLifecycle": one record per lifecycle stage (Story 25.2)
 *   - "dealsByPipeline": one record per pipeline (Story 25.3)
 *   - "dealsByOwner": one record per assigned deal owner (Story 25.3)
 *   - "dealsByType": one record per deal type (Story 25.3)
 *   - "contactsBySourceDetail": one record per {source, drilldown1} combination (Story 25.5)
 *   - "contactsByLatestSource": one record per hs_latest_source value (Story 25.5)
 *   - "dailyContactsBySource": one record per {date, source} for top 5 sources + "other" (Story 25.5)
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  AdapterRefreshContext,
  OAuthCredentials,
  AdapterResult,
  RefreshedTokens,
  MetricDefinition,
  DimensionDefinition,
  AdapterMetrics,
  AccountInfo,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";
import {
  fetchPipelines,
  fetchOwners,
  fetchCrmObjects,
  searchCrmObjects,
  fetchCrmObjectsWithScope,
  searchCrmObjectsWithScope,
  fetchEmailStatistics,
  fetchMarketingEmails,
  fetchEmailEvents,
  fetchTrafficTotals,
  fetchTrafficSources,
  fetchTrafficSessionsDaily,
  fetchCampaigns,
  fetchCampaignMetrics,
  fetchCampaignRevenue,
  fetchForms,
  fetchFormSubmissionsCount,
  fetchMarketingEvents,
  fetchWorkflows,
  fetchWorkflowDetails,
  fetchEventDefinitions,
  fetchCustomEventCount,
  FORM_SUBMISSION_BATCH_SIZE,
  FORM_SUBMISSION_BATCH_DELAY_MS,
  CAMPAIGN_BATCH_SIZE,
  CAMPAIGN_BATCH_DELAY_MS,
  WORKFLOW_BATCH_SIZE,
  WORKFLOW_BATCH_DELAY_MS,
  EVENT_TYPE_BATCH_SIZE,
  EVENT_TYPE_BATCH_DELAY_MS,
  MAX_EVENT_TYPES,
  EMAIL_EVENT_TYPES,
  MAX_PAGES,
  SEARCH_PAGE_SIZE,
  type EmailEventType,
  type MarketingEmailObject,
  type TrafficTotalsResponse,
  type TrafficSourcesResponse,
  type TrafficSessionsDailyResponse,
  type HubSpotCampaign,
  type CampaignMetricsReport,
  type CampaignRevenueReport,
  type HubSpotCrmObject,
  type HubSpotForm,
  type HubSpotMarketingEvent,
  type HubSpotWorkflow,
  type HubSpotWorkflowStats,
} from "./api";
import type { KVStore } from "../cache";
import { refreshHubSpotToken, verifyHubSpotAccessToken } from "./oauth";

// ============================================================================
// Metric & Dimension Definitions (AC #6)
// ============================================================================

export const HUBSPOT_METRICS: MetricDefinition[] = [
  // Contact metrics
  {
    key: "contacts_total",
    label: "Total Contacts",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "contacts_new",
    label: "New Contacts",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "mql_count",
    label: "Current MQLs",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    // R3-M1: Point-in-time snapshot, not funnel-compatible (contacts advance through stages)
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "sql_count",
    label: "Current SQLs",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "customer_count",
    label: "Current Customers",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // Deal metrics
  {
    key: "deals_total",
    label: "Total Deals",
    type: "number",
    category: "Deals",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "deals_new",
    label: "New Deals",
    type: "number",
    category: "Deals",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "deals_closed_won",
    label: "Closed Won",
    type: "number",
    category: "Deals",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "deals_closed_lost",
    label: "Closed Lost",
    type: "number",
    category: "Deals",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "win_rate",
    label: "Win Rate",
    type: "percentage",
    category: "Deals",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "deals_closed_won", denominator: "deals_total" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  // Revenue metrics
  {
    key: "pipeline_value",
    label: "Pipeline Value",
    type: "currency",
    category: "Revenue",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "revenue_closed_won",
    label: "Revenue (Closed Won)",
    type: "currency",
    category: "Revenue",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "avg_deal_amount",
    label: "Avg Deal Amount",
    type: "currency",
    category: "Revenue",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { denominator: "deals_total" },
    compatibleWidgets: ["kpi"],
  },
  {
    key: "deals_by_stage",
    label: "Deals by Stage",
    type: "number",
    category: "Pipeline",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["funnel", "bar-chart", "pie-chart"],
  },
  // Story 25.2: Contact expansion metrics
  {
    key: "subscriber_count",
    label: "Subscribers",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "lead_count",
    label: "Leads",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "opportunity_count",
    label: "Opportunities",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "avg_contact_score",
    label: "Avg Contact Score",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "ratio", ratioComponents: { denominator: "contacts_total" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "contacts_by_source",
    label: "Contacts by Source",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["pie-chart", "bar-chart"],
  },
  {
    key: "contacts_by_lifecycle",
    label: "Contacts by Lifecycle",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["pie-chart", "funnel", "bar-chart"],
  },
  // Story 25.3: Deal pipeline analytics metrics
  {
    key: "weighted_pipeline_value",
    label: "Weighted Pipeline Value",
    type: "currency",
    category: "Revenue",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "avg_days_to_close",
    label: "Avg Days to Close",
    type: "number",
    category: "Deals",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "deals_closed_won" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "largest_deal_amount",
    label: "Largest Deal Amount",
    type: "currency",
    category: "Revenue",
    dataType: "Currency",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "revenue_by_closedate",
    label: "Revenue by Close Date",
    type: "currency",
    category: "Revenue",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "deals_by_pipeline",
    label: "Deals by Pipeline",
    type: "number",
    category: "Pipeline",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "pie-chart"],
  },
  {
    key: "deals_by_owner",
    label: "Deals by Owner",
    type: "number",
    category: "Deals",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "data-table"],
  },
  {
    key: "deals_by_type",
    label: "Deals by Type",
    type: "number",
    category: "Deals",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["pie-chart", "bar-chart"],
  },
  {
    key: "unassigned_deals_count",
    label: "Unassigned Deals",
    type: "number",
    category: "Deals",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  // Story 25.5: Lead attribution metrics
  {
    key: "contacts_by_latest_source",
    label: "Contacts (Last Touch)",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart", "pie-chart", "data-table"],
  },
  // ==========================================================================
  // Story 25.6: HubSpot Email Statistics — 36 metrics
  // Requires `content` scope (SuperAdmin). Gracefully skipped when absent.
  // ==========================================================================
  // 16 aggregate counters (#15-#30)
  {
    key: "email_sent",
    label: "Emails Sent",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "email_delivered",
    label: "Emails Delivered",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
  },
  {
    key: "email_open",
    label: "Email Opens (Total)",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
  },
  {
    key: "email_click",
    label: "Email Clicks (Total)",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
  },
  {
    key: "email_hardbounced",
    label: "Hard Bounces",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
  },
  {
    key: "email_softbounced",
    label: "Soft Bounces",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
  },
  {
    key: "email_unsubscribed",
    label: "Unsubscribes",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
  },
  {
    key: "email_spamreport",
    label: "Spam Reports",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_dropped",
    label: "Dropped",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_notsent",
    label: "Not Sent",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_pending",
    label: "Pending",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_bounce",
    label: "Bounces (Total)",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_reply",
    label: "Replies",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_selected",
    label: "Selected",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_contactslost",
    label: "Contacts Lost",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_suppressed",
    label: "Suppressed",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  // 12 aggregate ratios (#31-#42) — stored as 0-1 decimals, widget formatter multiplies for %
  {
    key: "email_delivered_rate",
    label: "Delivery Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_delivered", denominator: "email_sent" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "email_open_rate",
    label: "Open Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_open", denominator: "email_delivered" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  {
    key: "email_click_rate",
    label: "Click Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_click", denominator: "email_delivered" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  {
    key: "email_clickthrough_rate",
    label: "Click-Through Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_click", denominator: "email_open" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "email_bounce_rate",
    label: "Bounce Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_bounce", denominator: "email_sent" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "email_hardbounce_rate",
    label: "Hard Bounce Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_hardbounced", denominator: "email_sent" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "email_softbounce_rate",
    label: "Soft Bounce Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_softbounced", denominator: "email_sent" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "email_unsubscribed_rate",
    label: "Unsubscribe Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_unsubscribed", denominator: "email_delivered" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "email_spamreport_rate",
    label: "Spam Report Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_spamreport", denominator: "email_delivered" },
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_notsent_rate",
    label: "Not Sent Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_notsent", denominator: "email_sent" },
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_pending_rate",
    label: "Pending Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_pending", denominator: "email_sent" },
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_contactslost_rate",
    label: "Contacts Lost Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "email_contactslost", denominator: "email_delivered" },
    compatibleWidgets: ["kpi"],
  },
  // Device breakdown (#43-#46) — open_count metric across computer/mobile/unknown records
  {
    key: "email_open_count_by_device",
    label: "Email Opens by Device",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["pie-chart", "bar-chart"],
  },
  {
    key: "email_open_computer",
    label: "Email Opens — Computer",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_open_mobile",
    label: "Email Opens — Mobile",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "email_open_unknown",
    label: "Email Opens — Unknown",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  // Per-email object fields (#64-#68 → 4 wireable ones as record fields)
  {
    key: "email_campaign_sent",
    label: "Email Campaign — Sent",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "email_campaign_open_rate",
    label: "Email Campaign — Open Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "email_campaign_sent" },
    compatibleWidgets: ["data-table"],
  },
  {
    key: "email_campaign_click_rate",
    label: "Email Campaign — Click Rate",
    type: "percentage",
    category: "Email",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "email_campaign_sent" },
    compatibleWidgets: ["data-table"],
  },
  {
    key: "email_event_count",
    label: "Email Event Count",
    type: "number",
    category: "Email",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "bar-chart", "data-table"],
  },
  // ==========================================================================
  // Story 25.7: HubSpot Traffic Analytics — 14 metrics (#1-#14)
  // Analytics v2 legacy API. Requires `content` scope. Gracefully skipped when absent.
  // ==========================================================================
  {
    key: "traffic_sessions",
    label: "Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
  },
  {
    key: "traffic_visitors",
    label: "Visitors",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
  },
  {
    key: "traffic_pageviews",
    label: "Page Views",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
  },
  {
    key: "traffic_pages_per_session",
    label: "Pages per Session",
    type: "number",
    category: "Traffic",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { numerator: "traffic_pageviews", denominator: "traffic_sessions" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "traffic_avg_session_duration",
    label: "Avg Session Duration",
    type: "duration",
    category: "Traffic",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "traffic_sessions" },
    compatibleWidgets: ["kpi"],
  },
  {
    key: "traffic_bounce_rate",
    label: "Bounce Rate",
    type: "percentage",
    category: "Traffic",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "traffic_sessions" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  {
    key: "traffic_new_visits",
    label: "New Visitor Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "traffic_returning_visits",
    label: "Return Visitor Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  {
    key: "traffic_new_visitor_pct",
    label: "New Visitor Percentage",
    type: "percentage",
    category: "Traffic",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "traffic_visitors" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  // 5 source-specific session counts (#10-#14)
  {
    key: "traffic_direct_sessions",
    label: "Direct Traffic Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "pie-chart"],
  },
  {
    key: "traffic_organic_sessions",
    label: "Organic Search Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "pie-chart"],
  },
  {
    key: "traffic_paid_sessions",
    label: "Paid Search Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "pie-chart"],
  },
  {
    key: "traffic_social_sessions",
    label: "Social Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "pie-chart"],
  },
  {
    key: "traffic_referral_sessions",
    label: "Referral Sessions",
    type: "number",
    category: "Traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "pie-chart"],
  },
  // ==========================================================================
  // Story 25.8: HubSpot Marketing Campaigns — 9 metrics (#55-#63)
  // Two endpoints per campaign: /reports/metrics (4) + /reports/revenue (5).
  // Requires `content` scope (SuperAdmin). Gracefully skipped when absent.
  // ==========================================================================
  // #55-#58: Engagement metrics from /reports/metrics
  {
    key: "campaign_sessions",
    label: "Campaign Sessions",
    type: "number",
    category: "Campaigns",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  {
    key: "campaign_new_contacts_first_touch",
    label: "New Contacts (First Touch)",
    type: "number",
    category: "Campaigns",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  {
    key: "campaign_new_contacts_last_touch",
    label: "New Contacts (Last Touch)",
    type: "number",
    category: "Campaigns",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  {
    key: "campaign_influenced_contacts",
    label: "Influenced Contacts",
    type: "number",
    category: "Campaigns",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  // #59-#63: Attribution metrics from /reports/revenue
  {
    key: "campaign_contacts_attributed",
    label: "Contacts Attributed",
    type: "number",
    category: "Campaigns",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart", "data-table"],
  },
  {
    key: "campaign_currency_code",
    label: "Currency Code",
    type: "number",
    category: "Campaigns",
    // String value (ISO currency code) rendered only in data-tables. Enum dataType
    // signals to widget renderers that the raw value is categorical, not numeric.
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "campaign_deal_amount",
    label: "Total Deal Amount",
    type: "currency",
    category: "Campaigns",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  {
    key: "campaign_deals_number",
    label: "Deals Attributed",
    type: "number",
    category: "Campaigns",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart", "data-table"],
  },
  {
    key: "campaign_revenue_amount",
    label: "Revenue Attributed",
    type: "currency",
    category: "Campaigns",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  // ==========================================================================
  // Story 25.9: Companies, Tickets & Engagements — 15 metrics (#97-#111)
  // Company metrics (#97-#101). Use `crm.objects.companies.read` (pre-25.9).
  // Ticket metrics (#102-#106). Require new `tickets` scope — scope_missing = 0.
  // Engagement summary metrics (#107-#111). Use `tickets` scope per HubSpot.
  // ==========================================================================
  // #97 Total Companies
  {
    key: "companies_total",
    label: "Total Companies",
    type: "number",
    category: "Companies",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #98 New Companies (period)
  {
    key: "companies_new",
    label: "New Companies",
    type: "number",
    category: "Companies",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #99 Companies by Industry — grouping metric (count per industry)
  {
    key: "companies_by_industry",
    label: "Companies by Industry",
    type: "number",
    category: "Companies",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "pie-chart", "data-table"],
  },
  // #100 Companies by Country — grouping metric
  {
    key: "companies_by_country",
    label: "Companies by Country",
    type: "number",
    category: "Companies",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "pie-chart", "data-table"],
  },
  // #101 Annual Revenue (sum across companies) — currency
  {
    key: "companies_annual_revenue",
    label: "Companies Annual Revenue",
    type: "currency",
    category: "Companies",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
  },
  // #102 Total Tickets
  {
    key: "tickets_total",
    label: "Total Tickets",
    type: "number",
    category: "Tickets",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #103 New Tickets (period)
  {
    key: "tickets_new",
    label: "New Tickets",
    type: "number",
    category: "Tickets",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #104 Tickets by Status — grouping metric (count per pipeline stage)
  {
    key: "tickets_by_status",
    label: "Tickets by Status",
    type: "number",
    category: "Tickets",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["pie-chart", "bar-chart", "data-table"],
  },
  // #105 Avg Time to First Response (seconds — adapter normalizes ms→s for display)
  {
    key: "avg_time_to_first_response",
    label: "Avg Time to First Response",
    type: "duration",
    category: "Tickets",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "tickets_total" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  // #106 Avg Time to Close
  {
    key: "avg_time_to_close",
    label: "Avg Time to Close",
    type: "duration",
    category: "Tickets",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "tickets_total" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  // #107 Total Calls
  {
    key: "total_calls",
    label: "Total Calls",
    type: "number",
    category: "Engagements",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #108 Total Meetings Booked
  {
    key: "total_meetings",
    label: "Total Meetings",
    type: "number",
    category: "Engagements",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #109 Total Emails Sent (CRM-tracked)
  {
    key: "total_emails_crm",
    label: "Total Emails (CRM)",
    type: "number",
    category: "Engagements",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #110 Total Notes Logged
  {
    key: "total_notes",
    label: "Total Notes",
    type: "number",
    category: "Engagements",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #111 Total Tasks Completed
  {
    key: "total_tasks_completed",
    label: "Completed Tasks",
    type: "number",
    category: "Engagements",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // ==========================================================================
  // Story 25.10: Forms & Marketing Events — 8 metrics (#112-#119)
  // Forms require `forms` optional scope; events require
  // `crm.objects.marketing_events.read` optional scope. Gracefully skipped if absent.
  // ==========================================================================
  // #112 Total Form Submissions — sum across all forms in period
  {
    key: "total_form_submissions",
    label: "Total Form Submissions",
    type: "number",
    category: "Forms",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #113 Form Submissions (per form, via formSubmissions reportType)
  {
    key: "form_submissions_count",
    label: "Form Submissions",
    type: "number",
    category: "Forms",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "data-table"],
  },
  // #114 Form Conversion Rate — submissions / views (null when views unavailable)
  {
    key: "form_conversion_rate",
    label: "Form Conversion Rate",
    type: "percentage",
    category: "Forms",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "form_submissions_count", denominator: "form_views" },
    compatibleWidgets: ["kpi", "gauge", "data-table"],
  },
  // #115 Form Views — exposed for future extension (null today per AC #1 note)
  {
    key: "form_views",
    label: "Form Views",
    type: "number",
    category: "Forms",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
  },
  // #116 Total Event Registrations — sum of registrants across events in period
  {
    key: "total_event_registrations",
    label: "Total Event Registrations",
    type: "number",
    category: "Events",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #117 Total Event Attendees
  {
    key: "total_event_attendees",
    label: "Total Event Attendees",
    type: "number",
    category: "Events",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #118 Event Attendance Rate — attendees / registrants, stored as 0-1 decimal
  {
    key: "event_attendance_rate",
    label: "Event Attendance Rate",
    type: "percentage",
    category: "Events",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "total_event_attendees", denominator: "total_event_registrations" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  // #119 Total Cancellations
  {
    key: "total_cancellations",
    label: "Total Cancellations",
    type: "number",
    category: "Events",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
  },
  // ==========================================================================
  // Story 25.11: Workflows (Automation v4 BETA), Custom Events, Remaining CRM.
  //
  // Workflow metrics require `automation` scope; custom events require
  // `behavioral_events.event_definitions.read_write`. Both are optional —
  // graceful skip + flag when absent. Remaining CRM metrics (days_to_close,
  // email domain distribution, forecast probability) piggyback on existing
  // `crm.objects.{contacts,deals}.read` scopes.
  // ==========================================================================
  // #120 Total Workflow Enrollments (sum across workflows)
  {
    key: "total_workflow_enrollments",
    label: "Total Workflow Enrollments",
    type: "number",
    category: "Workflows",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #121 Active Enrollments
  {
    key: "active_enrollments",
    label: "Active Enrollments",
    type: "number",
    category: "Workflows",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  // #122 Completed Enrollments
  {
    key: "completed_enrollments",
    label: "Completed Enrollments",
    type: "number",
    category: "Workflows",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  // #123 Workflow Goal Conversion Rate (derived: completed / total, stored 0-1)
  {
    key: "workflow_goal_conversion_rate",
    label: "Workflow Goal Conversion Rate",
    type: "percentage",
    category: "Workflows",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "total_workflow_enrollments" },
    compatibleWidgets: ["kpi", "gauge"],
  },
  // #124 Avg Time in Workflow (milliseconds; widget formatter converts for display)
  {
    key: "avg_time_in_workflow",
    label: "Avg Time in Workflow",
    type: "duration",
    category: "Workflows",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "completed_enrollments" },
    compatibleWidgets: ["kpi"],
  },
  // #125 Custom Event Count (per type, surfaced via customEvents reportType records)
  {
    key: "custom_event_count",
    label: "Custom Event Count",
    type: "number",
    category: "Custom Events",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // #126 Custom Event Properties — object; surfaced via record fields in data-table only
  {
    key: "custom_event_properties",
    label: "Custom Event Properties",
    type: "number",
    category: "Custom Events",
    // Enum because the raw value is a JSON object (per AC text). Widget renderers
    // decide formatting; adapter never surfaces it as a numeric aggregate.
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
  },
  // #127 Custom Event Timestamp — latest occurrence per event type.
  // Stored as an ISO 8601 string (e.g. "2026-01-15T10:00:00Z"); widgets render
  // it through a date formatter. dataType "Enum" signals non-numeric value to
  // widget renderers that key off numeric dataTypes for aggregations.
  {
    key: "custom_event_latest_timestamp",
    label: "Latest Event Timestamp",
    type: "number",
    category: "Custom Events",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
  },
  // #79 Avg Days to Close (contact-level; complements deal-level avg_days_to_close)
  {
    key: "avg_days_to_close_contact",
    label: "Avg Days to Close (Contact)",
    type: "number",
    category: "Contacts",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "customer_count" },
    compatibleWidgets: ["kpi"],
  },
  // #80 Contacts per Email Domain — surfaced via contactsByEmailDomain reportType
  {
    key: "contacts_by_email_domain",
    label: "Contacts by Email Domain",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "pie-chart", "data-table"],
  },
  // #92 Deal Forecast Probability — per-deal percentage (stored 0-1, widget formatter renders %)
  {
    key: "deal_forecast_probability",
    label: "Deal Forecast Probability",
    type: "percentage",
    category: "Deals",
    dataType: "Percentage",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table", "gauge"],
  },
  // Geography metrics (#14-#18 referenced as dims, but agency reports need a
  // bound metric to render the bar-chart). Count-per-bucket surfaced via
  // contactsByCountry + contactsByCity reportTypes.
  {
    key: "contacts_by_country",
    label: "Contacts by Country",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "pie-chart", "data-table"],
  },
  {
    key: "contacts_by_city",
    label: "Contacts by City",
    type: "number",
    category: "Contacts",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart", "data-table"],
  },
];

export const HUBSPOT_DIMENSIONS: DimensionDefinition[] = [
  { key: "date", label: "Date", type: "Time" },
  { key: "deal_stage", label: "Deal Stage", type: "Structure" },
  { key: "pipeline", label: "Pipeline", type: "Structure" },
  { key: "lifecycle_stage", label: "Lifecycle Stage", type: "Structure" },
  { key: "deal_owner", label: "Deal Owner", type: "Structure" },
  { key: "deal_type", label: "Deal Type", type: "Classification" },
  { key: "source", label: "Contact Source", type: "Classification" },
  { key: "owner", label: "Deal Owner", type: "Classification" },
  // Story 25.5: Attribution dimensions
  { key: "source_detail", label: "Source Detail", type: "Classification" },
  { key: "latest_source", label: "Latest Source", type: "Classification" },
  // Story 25.6: Email dimensions (AC #8)
  { key: "email_name", label: "Email Name", type: "Content" },
  { key: "email_type", label: "Email Type", type: "Classification" },
  { key: "email_state", label: "Email State", type: "Classification" },
  { key: "email_send_date", label: "Email Send Date", type: "Time" },
  { key: "device_type", label: "Device Type", type: "Tech" },
  // Story 25.7: Traffic dimensions (AC #6)
  { key: "traffic_source_type", label: "Traffic Source Type", type: "Traffic" },
  { key: "utm_source", label: "UTM Source", type: "Traffic" },
  { key: "utm_medium", label: "UTM Medium", type: "Traffic" },
  { key: "utm_campaign", label: "UTM Campaign", type: "Traffic" },
  { key: "utm_content", label: "UTM Content", type: "Traffic" },
  { key: "utm_term", label: "UTM Term", type: "Traffic" },
  { key: "week", label: "Week", type: "Time" },
  { key: "month", label: "Month", type: "Time" },
  // Story 25.8: Marketing Campaign dimensions (#42-#44)
  { key: "campaign_name", label: "Campaign Name", type: "Structure" },
  { key: "campaign_owner", label: "Campaign Owner", type: "Structure" },
  { key: "campaign_type", label: "Campaign Type", type: "Classification" },
  // Story 25.9: Company attribute dimensions (#32-#36)
  { key: "industry", label: "Industry", type: "Classification" },
  { key: "company_size", label: "Company Size", type: "Classification" },
  { key: "annual_revenue_range", label: "Annual Revenue Range", type: "Classification" },
  { key: "company_lifecycle_stage", label: "Company Lifecycle Stage", type: "Classification" },
  { key: "company_owner", label: "Company Owner", type: "Structure" },
  // Story 25.9: Ticket + country dimensions for grouping
  { key: "ticket_status", label: "Ticket Status", type: "Classification" },
  { key: "country", label: "Country", type: "Geo" },
  // Story 25.10: Forms + Events dimensions (directive #45-46 + events)
  { key: "form_name", label: "Form Name", type: "Content" },
  { key: "form_id", label: "Form ID", type: "Content" },
  { key: "event_name", label: "Event Name", type: "Structure" },
  { key: "event_status", label: "Event Status", type: "Classification" },
  // Story 25.11: Workflow + Custom Events + Forecast + Geography dimensions
  // (#14 country already present above). Add #15 state, #16 city, #30 forecast
  // category, plus workflow + event_type + email_domain (drilldown for #80).
  { key: "state", label: "State / Region", type: "Geo" },
  { key: "city", label: "City", type: "Geo" },
  { key: "forecast_category", label: "Forecast Category", type: "Classification" },
  { key: "workflow_name", label: "Workflow Name", type: "Structure" },
  { key: "workflow_id", label: "Workflow ID", type: "Structure" },
  { key: "event_type", label: "Event Type", type: "Classification" },
  { key: "email_domain", label: "Email Domain", type: "Content" },
];

// ============================================================================
// Lifecycle stage constants (defaults — customizable per portal)
// ============================================================================

const SUBSCRIBER_STAGE = "subscriber";
const LEAD_STAGE = "lead";
const MQL_STAGE = "marketingqualifiedlead";
const SQL_STAGE = "salesqualifiedlead";
const OPPORTUNITY_STAGE = "opportunity";
const CUSTOMER_STAGE = "customer";

/**
 * Normalize HubSpot hs_analytics_source enum to lowercase snake_case.
 * Unknown/null values map to "unknown".
 *
 * HubSpot returns SCREAMING_SNAKE_CASE values (e.g., ORGANIC_SEARCH, PAID_SOCIAL).
 * Simple toLowerCase() is sufficient because the source already uses underscores.
 * If HubSpot ever changes to camelCase, this function would need updating.
 */
function normalizeSource(raw: string | null | undefined): string {
  if (!raw) return "unknown";
  return raw.toLowerCase();
}

// Deal stage metadata keys for closed-won/lost detection
const CLOSED_WON_PROBABILITY = "1.0";
const CLOSED_LOST_PROBABILITY = "0.0";

// ============================================================================
// Story 25.6: Email Statistics constants
// ============================================================================

/** Known aggregate counter field names returned by /marketing/v3/emails/statistics/list. */
const EMAIL_COUNTERS = [
  "sent",
  "delivered",
  "open",
  "click",
  "hardbounced",
  "softbounced",
  "unsubscribed",
  "spamreport",
  "dropped",
  "notsent",
  "pending",
  "bounce",
  "reply",
  "selected",
  "contactslost",
  "suppressed",
] as const;

/**
 * Known aggregate ratio field names → target summary keys.
 * Keys are stored as 0-1 decimals; widget formatter multiplies by 100 for display.
 */
const EMAIL_RATIO_KEYS: ReadonlyArray<readonly [string, string]> = [
  ["deliveredratio", "email_delivered_rate"],
  ["openratio", "email_open_rate"],
  ["clickratio", "email_click_rate"],
  ["clickthroughratio", "email_clickthrough_rate"],
  ["bounceratio", "email_bounce_rate"],
  ["hardbounceratio", "email_hardbounce_rate"],
  ["softbounceratio", "email_softbounce_rate"],
  ["unsubscribedratio", "email_unsubscribed_rate"],
  ["spamreportratio", "email_spamreport_rate"],
  ["notsentratio", "email_notsent_rate"],
  ["pendingratio", "email_pending_rate"],
  ["contactslostratio", "email_contactslost_rate"],
];

/** Device types tracked in emailDeviceBreakdown reportType. */
const EMAIL_DEVICE_TYPES = ["computer", "mobile", "unknown"] as const;

/** KV cache TTL for remembered "content scope missing" flag (24h). */
const EMAIL_SCOPE_CACHE_TTL_SECONDS = 86400;

// ============================================================================
// Story 25.7: Traffic Analytics constants
// ============================================================================

/**
 * Map HubSpot Analytics v2 source enum → our normalized `source_type` and
 * (when relevant) summary key for per-source session counts.
 *
 * HubSpot returns SCREAMING_SNAKE_CASE enum values. We normalize them to
 * lowercase tokens matching the directive spec (`organic`, `paid`, `direct`, etc)
 * while still capturing the original enum as the dimension value for groupBy.
 */
type TrafficSourceBucket = "direct" | "organic" | "paid" | "social" | "referral" | "email" | "other";

// R1-M2: Map for O(1) enum→bucket lookup (was linear array scan)
const TRAFFIC_SOURCE_BUCKET_MAP: ReadonlyMap<string, TrafficSourceBucket> = new Map<string, TrafficSourceBucket>([
  ["DIRECT_TRAFFIC", "direct"],
  ["DIRECT", "direct"],
  ["ORGANIC_SEARCH", "organic"],
  ["ORGANIC", "organic"],
  ["PAID_SEARCH", "paid"],
  ["PAID_SOCIAL", "paid"],
  ["SOCIAL_MEDIA", "social"],
  ["ORGANIC_SOCIAL", "social"],
  ["SOCIAL", "social"],
  ["REFERRALS", "referral"],
  ["REFERRAL", "referral"],
  ["EMAIL_MARKETING", "email"],
  ["EMAIL", "email"],
  ["OTHER_CAMPAIGNS", "other"],
  ["OFFLINE", "other"],
  ["OTHER", "other"],
]);

/** Summary keys for per-source session counts (AC #5, metrics #10-#14). */
const TRAFFIC_SOURCE_SUMMARY_KEYS: Record<TrafficSourceBucket, string | undefined> = {
  direct: "traffic_direct_sessions",
  organic: "traffic_organic_sessions",
  paid: "traffic_paid_sessions",
  social: "traffic_social_sessions",
  referral: "traffic_referral_sessions",
  // email + other have no dedicated summary metric (reach via trafficBySource reportType only)
  email: undefined,
  other: undefined,
};

/** KV cache TTL for remembered "traffic scope missing" flag (24h). */
const TRAFFIC_SCOPE_CACHE_TTL_SECONDS = 86400;

/**
 * Normalize a HubSpot source enum value (e.g. `ORGANIC_SEARCH`) to one of 7
 * canonical buckets. Unknown values collapse to "other" — we never drop data
 * silently.
 */
function normalizeTrafficSource(raw: string): TrafficSourceBucket {
  return TRAFFIC_SOURCE_BUCKET_MAP.get(raw.toUpperCase()) ?? "other";
}

/** Coerce a raw JSON number to a finite non-negative number; null/NaN/Inf → 0. */
function trafficNumber(raw: number | null | undefined): number {
  if (typeof raw !== "number" || !isFinite(raw) || raw < 0) return 0;
  return raw;
}

/**
 * Story 25.7: Extract 8 core metrics + derive new_visitor_pct from the totals
 * response. Returns an object that callers spread onto the summary record.
 * Ratios returned by the API may be 0-1 or 0-100 — normalize to 0-1.
 */
function processTrafficTotals(
  data: TrafficTotalsResponse | undefined,
): Record<string, number> {
  const totals = data?.totals ?? {};
  const sessions = Math.round(trafficNumber(totals.visits));
  const visitors = Math.round(trafficNumber(totals.visitors));
  const pageviews = Math.round(trafficNumber(totals.pageviews));
  const pagesPerSession = Math.round(trafficNumber(totals.pagesPerVisit) * 100) / 100;
  const avgSessionDuration = Math.round(trafficNumber(totals.timePerVisit) * 100) / 100;

  const rawBounce = trafficNumber(totals.bounceRate);
  const bounceRate = Math.round((rawBounce > 1 ? rawBounce / 100 : rawBounce) * 10000) / 10000;

  const newVisits = Math.round(trafficNumber(totals.newVisits));
  const returningVisits = Math.round(trafficNumber(totals.returningVisits));
  const newVisitorPct = sessions > 0 ? Math.round((newVisits / sessions) * 10000) / 10000 : 0;

  return {
    traffic_sessions: sessions,
    traffic_visitors: visitors,
    traffic_pageviews: pageviews,
    traffic_pages_per_session: pagesPerSession,
    traffic_avg_session_duration: avgSessionDuration,
    traffic_bounce_rate: bounceRate,
    traffic_new_visits: newVisits,
    traffic_returning_visits: returningVisits,
    traffic_new_visitor_pct: newVisitorPct,
  };
}

/**
 * Shape of a single traffic data row returned by Analytics v2. Shared across
 * source breakdowns and daily sessions.
 */
interface TrafficRow {
  readonly visits?: number | null;
  readonly visitors?: number | null;
  readonly pageviews?: number | null;
  readonly bounceRate?: number | null;
}

/**
 * R1-H1/M8: Analytics v2's `sources/daily` endpoint can return two top-level
 * shapes depending on portal settings and API version:
 *   (A) `{ breakdowns: { ORGANIC_SEARCH: [...rows], DIRECT_TRAFFIC: [...rows] } }`
 *   (B) `{ breakdowns: { "20260101": { ORGANIC_SEARCH: {...row}, ... }, ... } }`
 * Normalize to (A) so downstream aggregation only needs a single path.
 * Date keys match `^\d{8,10}$` (YYYYMMDD or YYYYMMDDHH).
 */
function flattenSourceBreakdowns(
  breakdowns: Record<string, unknown>,
): Record<string, TrafficRow[]> {
  const flat: Record<string, TrafficRow[]> = {};
  const DATE_KEY = /^\d{8,10}$/;

  // R2-H1: Coerce a value into TrafficRow[]. Only plain objects/arrays pass;
  // primitives and null fall through as [] so downstream code doesn't get
  // surprised by `row.visits` being undefined on a number.
  const toRows = (v: unknown): TrafficRow[] => {
    if (Array.isArray(v)) return v.filter((r): r is TrafficRow => r != null && typeof r === "object") as TrafficRow[];
    if (v != null && typeof v === "object") return [v as TrafficRow];
    return [];
  };

  for (const [outerKey, outerValue] of Object.entries(breakdowns)) {
    if (outerValue == null) continue;

    // Shape B: outer key is a date → iterate nested source keys
    if (DATE_KEY.test(outerKey) && typeof outerValue === "object" && !Array.isArray(outerValue)) {
      for (const [sourceKey, sourceValue] of Object.entries(outerValue as Record<string, unknown>)) {
        const rows = toRows(sourceValue);
        if (rows.length === 0) continue;
        (flat[sourceKey] ??= []).push(...rows);
      }
      continue;
    }

    // Shape A: outer key is a source enum → value is rows[] or a single totals object
    const rows = toRows(outerValue);
    if (rows.length === 0) continue;
    (flat[outerKey] ??= []).push(...rows);
  }
  return flat;
}

/**
 * Story 25.7: Aggregate source breakdown → trafficBySource reportType records.
 * Accepts both known response shapes (see flattenSourceBreakdowns).
 *
 * Returns both the records and the per-bucket session totals so the summary
 * record gets populated with `traffic_{bucket}_sessions` keys.
 */
function processTrafficSources(
  data: TrafficSourcesResponse | undefined,
): {
  records: Record<string, unknown>[];
  bucketSessions: Record<TrafficSourceBucket, number>;
} {
  const bucketSessions: Record<TrafficSourceBucket, number> = {
    direct: 0,
    organic: 0,
    paid: 0,
    social: 0,
    referral: 0,
    email: 0,
    other: 0,
  };
  const bucketVisitors: Record<TrafficSourceBucket, number> = { ...bucketSessions };
  const bucketPageviews: Record<TrafficSourceBucket, number> = { ...bucketSessions };
  // R1-M3: Sessions-weighted bounce rate (not simple mean). Accumulate
  // (bounceRate * visits) numerator + visits denominator per bucket.
  const bucketBounceNum: Record<TrafficSourceBucket, number> = { ...bucketSessions };
  const bucketBounceDen: Record<TrafficSourceBucket, number> = { ...bucketSessions };

  const rawBreakdowns = (data?.breakdowns ?? {}) as Record<string, unknown>;
  const flat = flattenSourceBreakdowns(rawBreakdowns);

  for (const [rawKey, rows] of Object.entries(flat)) {
    const bucket = normalizeTrafficSource(rawKey);
    for (const row of rows) {
      const visits = Math.round(trafficNumber(row.visits));
      bucketSessions[bucket] += visits;
      bucketVisitors[bucket] += Math.round(trafficNumber(row.visitors));
      bucketPageviews[bucket] += Math.round(trafficNumber(row.pageviews));
      const rb = trafficNumber(row.bounceRate);
      if (visits > 0 && rb > 0) {
        const normalized = rb > 1 ? rb / 100 : rb;
        bucketBounceNum[bucket] += normalized * visits;
        bucketBounceDen[bucket] += visits;
      }
    }
  }

  const records: Record<string, unknown>[] = [];
  const buckets = Object.keys(bucketSessions) as TrafficSourceBucket[];
  for (const bucket of buckets) {
    if (bucketSessions[bucket] === 0 && bucketVisitors[bucket] === 0 && bucketPageviews[bucket] === 0) continue;
    const den = bucketBounceDen[bucket];
    const avgBounce = den > 0 ? Math.round((bucketBounceNum[bucket] / den) * 10000) / 10000 : 0;
    // R3-H1: Emit both short and traffic_-prefixed field names so pie-chart
    // presets can use either `sessions` (preset preference) or the registry
    // keys like `traffic_sessions` / `traffic_visitors`.
    records.push({
      _reportType: "trafficBySource",
      source_type: bucket,
      sessions: bucketSessions[bucket],
      visitors: bucketVisitors[bucket],
      pageviews: bucketPageviews[bucket],
      bounce_rate: avgBounce,
      traffic_sessions: bucketSessions[bucket],
      traffic_visitors: bucketVisitors[bucket],
      traffic_pageviews: bucketPageviews[bucket],
      traffic_bounce_rate: avgBounce,
    });
  }
  records.sort((a, b) => (b.sessions as number) - (a.sessions as number));

  return { records, bucketSessions };
}

/**
 * Story 25.7: Convert sessions/daily response → dailyTraffic reportType records.
 * Response is keyed by `YYYYMMDD` strings; we reformat to `YYYY-MM-DD` ISO.
 */
function processTrafficSessionsDaily(
  data: TrafficSessionsDailyResponse | undefined,
): Record<string, unknown>[] {
  if (!data || typeof data !== "object") return [];

  // R1-M4: Accept both YYYYMMDD (daily) and YYYYMMDDHH (hourly) keys. When
  // hourly keys appear, aggregate to per-day records (sessions-weighted
  // bounce rate). Non-digit keys (vendor metadata) are rejected.
  type DailyAcc = { sessions: number; visitors: number; pageviews: number; bounceNum: number; bounceDen: number };
  const perDate = new Map<string, DailyAcc>();

  for (const [rawKey, value] of Object.entries(data)) {
    if (!value || typeof value !== "object") continue;
    if (!/^\d{8,10}$/.test(rawKey)) continue;
    const isoDate = `${rawKey.slice(0, 4)}-${rawKey.slice(4, 6)}-${rawKey.slice(6, 8)}`;
    const visits = Math.round(trafficNumber(value.visits));
    const visitors = Math.round(trafficNumber(value.visitors));
    const pageviews = Math.round(trafficNumber(value.pageviews));
    const rawBounce = trafficNumber(value.bounceRate);
    const normalizedBounce = rawBounce > 1 ? rawBounce / 100 : rawBounce;

    let acc = perDate.get(isoDate);
    if (!acc) {
      acc = { sessions: 0, visitors: 0, pageviews: 0, bounceNum: 0, bounceDen: 0 };
      perDate.set(isoDate, acc);
    }
    acc.sessions += visits;
    acc.visitors += visitors;
    acc.pageviews += pageviews;
    if (visits > 0 && normalizedBounce > 0) {
      acc.bounceNum += normalizedBounce * visits;
      acc.bounceDen += visits;
    }
  }

  const records: Record<string, unknown>[] = [];
  const sortedDates = [...perDate.keys()].sort();
  for (const date of sortedDates) {
    const acc = perDate.get(date)!;
    const bounce = acc.bounceDen > 0 ? Math.round((acc.bounceNum / acc.bounceDen) * 10000) / 10000 : 0;
    // R3-H1: Keys match both forms so line-chart presets can use EITHER
    // `traffic_sessions` (matches HUBSPOT_METRICS registry + summary record)
    // OR `sessions` (short form used in widget preset configs).
    records.push({
      _reportType: "dailyTraffic",
      date,
      sessions: acc.sessions,
      visitors: acc.visitors,
      pageviews: acc.pageviews,
      bounce_rate: bounce,
      traffic_sessions: acc.sessions,
      traffic_visitors: acc.visitors,
      traffic_pageviews: acc.pageviews,
      traffic_bounce_rate: bounce,
    });
  }
  return records;
}

// ============================================================================
// Story 25.8: Marketing Campaigns constants + helpers
// ============================================================================

/** KV cache TTL for remembered "campaigns scope missing" flag (24h). */
const CAMPAIGN_SCOPE_CACHE_TTL_SECONDS = 86400;

/** Zero-values for campaign summary aggregate fields (AC #4). */
const CAMPAIGN_ZERO_SUMMARY: Record<string, number> = {
  campaign_sessions: 0,
  campaign_new_contacts_first_touch: 0,
  campaign_new_contacts_last_touch: 0,
  campaign_influenced_contacts: 0,
  campaign_contacts_attributed: 0,
  campaign_deal_amount: 0,
  campaign_deals_number: 0,
  campaign_revenue_amount: 0,
};

/** Coerce to finite non-negative number; null/NaN/Infinity/negative → 0. */
function campaignNumber(raw: number | null | undefined): number {
  if (typeof raw !== "number" || !isFinite(raw) || raw < 0) return 0;
  return raw;
}

/** Round Currency value to 2 decimals. */
function campaignCurrency(raw: number | null | undefined): number {
  const n = campaignNumber(raw);
  return Math.round(n * 100) / 100;
}

/**
 * Story 25.8: Sleep helper for rate-limit pacing between campaign batches.
 * Exposed via the adapter's internal call site so tests can stub `setTimeout`.
 */
function campaignSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Story 25.8: Process fetched campaigns + per-campaign reports into
 * campaignPerformance records + aggregate summary fields.
 *
 * Returns the records and the summary deltas to merge into the summary record.
 * Currency: we pin to the first non-empty currencyCode returned across all
 * campaigns. If campaigns have mixed currencies (rare) we flag it so the
 * consuming widget can disclaim the aggregate.
 */
function processCampaigns(
  campaigns: HubSpotCampaign[],
  reports: Array<{
    campaign: HubSpotCampaign;
    metrics: CampaignMetricsReport;
    revenue: CampaignRevenueReport;
  }>,
): {
  records: Record<string, unknown>[];
  summaryDelta: Record<string, unknown>;
} {
  const records: Record<string, unknown>[] = [];
  // Explicit shape so TS tracks every aggregate field as a defined number —
  // `Record<string, number>` tracks indexed access as possibly undefined.
  const agg = {
    campaign_sessions: 0,
    campaign_new_contacts_first_touch: 0,
    campaign_new_contacts_last_touch: 0,
    campaign_influenced_contacts: 0,
    campaign_contacts_attributed: 0,
    campaign_deal_amount: 0,
    campaign_deals_number: 0,
    campaign_revenue_amount: 0,
  };
  let firstCurrency: string | null = null;
  let mixedCurrency = false;

  for (const { campaign, metrics, revenue } of reports) {
    const sessions = Math.round(campaignNumber(metrics.sessions));
    const firstTouch = Math.round(campaignNumber(metrics.newContactsFirstTouch));
    const lastTouch = Math.round(campaignNumber(metrics.newContactsLastTouch));
    const influenced = Math.round(campaignNumber(metrics.influencedContacts));

    const contactsAttributed = Math.round(campaignNumber(revenue.contactsNumber));
    const dealAmount = campaignCurrency(revenue.dealAmount);
    const dealsNumber = Math.round(campaignNumber(revenue.dealsNumber));
    const revenueAmount = campaignCurrency(revenue.revenueAmount);
    const currencyCode =
      typeof revenue.currencyCode === "string" && revenue.currencyCode.trim() !== ""
        ? revenue.currencyCode.trim().toUpperCase()
        : "";

    if (currencyCode) {
      if (firstCurrency === null) {
        firstCurrency = currencyCode;
      } else if (firstCurrency !== currencyCode) {
        mixedCurrency = true;
      }
    }

    agg.campaign_sessions += sessions;
    agg.campaign_new_contacts_first_touch += firstTouch;
    agg.campaign_new_contacts_last_touch += lastTouch;
    agg.campaign_influenced_contacts += influenced;
    agg.campaign_contacts_attributed += contactsAttributed;
    agg.campaign_deal_amount += dealAmount;
    agg.campaign_deals_number += dealsNumber;
    agg.campaign_revenue_amount += revenueAmount;

    const name = campaign.properties?.hs_name?.trim() || campaign.id;
    const owner = campaign.properties?.hs_owner?.trim() || "";
    const campaignType = campaign.properties?.hs_type?.trim() || "";

    records.push({
      _reportType: "campaignPerformance",
      campaign_guid: campaign.id,
      campaign_name: name,
      campaign_owner: owner,
      campaign_type: campaignType,
      // 4 engagement metrics (#55-58)
      campaign_sessions: sessions,
      campaign_new_contacts_first_touch: firstTouch,
      campaign_new_contacts_last_touch: lastTouch,
      campaign_influenced_contacts: influenced,
      // 5 revenue metrics (#59-63)
      campaign_contacts_attributed: contactsAttributed,
      campaign_currency_code: currencyCode,
      campaign_deal_amount: dealAmount,
      campaign_deals_number: dealsNumber,
      campaign_revenue_amount: revenueAmount,
    });
  }

  // R1-M2: Sort descending by revenue (preset expectation: "sorted by revenue
  // desc"). Ties (common when multiple zero-revenue campaigns exist) keep the
  // order returned by HubSpot, which is typically most-recently-updated first.
  // Note: groupBy=campaign_name downstream will collide entries if two
  // campaigns share the exact same display name — rare; campaign_guid is
  // included on every record so consumers needing uniqueness can disambiguate.
  records.sort(
    (a, b) =>
      (b.campaign_revenue_amount as number) - (a.campaign_revenue_amount as number),
  );

  // R1-M1: Round currency aggregates ONCE here (per-campaign values are
  // already rounded, so summing introduces only bounded FP drift; a single
  // final round clamps the summary cleanly).
  const summaryDelta: Record<string, unknown> = {
    campaign_sessions: agg.campaign_sessions,
    campaign_new_contacts_first_touch: agg.campaign_new_contacts_first_touch,
    campaign_new_contacts_last_touch: agg.campaign_new_contacts_last_touch,
    campaign_influenced_contacts: agg.campaign_influenced_contacts,
    campaign_contacts_attributed: agg.campaign_contacts_attributed,
    campaign_deal_amount: Math.round(agg.campaign_deal_amount * 100) / 100,
    campaign_deals_number: agg.campaign_deals_number,
    campaign_revenue_amount: Math.round(agg.campaign_revenue_amount * 100) / 100,
    campaign_currency_code: firstCurrency ?? "",
    campaign_currency_mixed: mixedCurrency,
    campaigns_count: campaigns.length,
  };

  return { records, summaryDelta };
}

/** Zero-values for all traffic summary fields (AC #1: graceful skip). */
const TRAFFIC_ZERO_SUMMARY: Record<string, number> = {
  traffic_sessions: 0,
  traffic_visitors: 0,
  traffic_pageviews: 0,
  traffic_pages_per_session: 0,
  traffic_avg_session_duration: 0,
  traffic_bounce_rate: 0,
  traffic_new_visits: 0,
  traffic_returning_visits: 0,
  traffic_new_visitor_pct: 0,
  traffic_direct_sessions: 0,
  traffic_organic_sessions: 0,
  traffic_paid_sessions: 0,
  traffic_social_sessions: 0,
  traffic_referral_sessions: 0,
};

/**
 * Requested page size for /marketing/v3/emails. HubSpot's hard cap is 100 per page
 * (enforced inside `fetchMarketingEmails`); value here is the "requested" limit.
 * Total emails fetched = up to MAX_PAGES (20) * 100 = 2000 per fetch cycle.
 */
const EMAIL_CAMPAIGNS_PAGE_SIZE = 100;

// ============================================================================
// Story 25.9: Companies, Tickets & Engagements constants + helpers
// ============================================================================

/** KV cache TTL for remembered scope-missing flags (24h). */
const CRM_SCOPE_CACHE_TTL_SECONDS = 86400;

/** Company properties the adapter reads per AC #1. */
const COMPANY_PROPERTIES = [
  "createdate",
  "industry",
  "country",
  "annualrevenue",
  "numberofemployees",
  "lifecyclestage",
  "hubspot_owner_id",
] as const;

/** Ticket properties the adapter reads per AC #2. */
const TICKET_PROPERTIES = [
  "createdate",
  "hs_pipeline_stage",
  "time_to_first_response",
  "time_to_close",
] as const;

/** Completed task status per AC #3. */
const TASK_COMPLETED_STATUS = "COMPLETED";

/** Zero-values for all Story 25.9 summary fields (AC #1-#3: graceful skip default). */
const CRM_ZERO_SUMMARY: Record<string, number> = {
  companies_total: 0,
  companies_new: 0,
  companies_annual_revenue: 0,
  tickets_total: 0,
  tickets_new: 0,
  avg_time_to_first_response: 0,
  avg_time_to_close: 0,
  total_calls: 0,
  total_meetings: 0,
  total_emails_crm: 0,
  total_notes: 0,
  total_tasks_completed: 0,
};

/** Parse a HubSpot property string (always returned as string) to number. */
function crmParseStringNumber(raw: string | null | undefined): number {
  if (raw == null || raw === "") return 0;
  const n = parseFloat(raw);
  return isNaN(n) || !isFinite(n) || n < 0 ? 0 : n;
}

/**
 * Parse a HubSpot `createdate` property value into epoch milliseconds.
 * HubSpot v3 responses inconsistently return createdate as either:
 *   - ISO 8601 string ("2026-01-05T10:30:00Z") — list endpoints
 *   - Epoch millisecond string ("1705660800000") — search endpoints
 * Returns NaN when the value can't be parsed. (R3-M2)
 */
function crmParseCreateDate(raw: string | null | undefined): number {
  if (raw == null || raw === "") return NaN;
  // Epoch-ms string: digits only (may be signed for pre-1970, rare).
  if (/^-?\d+$/.test(raw)) {
    const ms = Number(raw);
    return isFinite(ms) ? ms : NaN;
  }
  // Otherwise fall back to Date.parse for ISO 8601.
  return Date.parse(raw);
}

/**
 * Build companiesByIndustry + companiesByCountry reportType records plus a
 * summary delta with companies_total, companies_new, and companies_annual_revenue.
 *
 * - companies_total uses the unfiltered list length.
 * - companies_new filters by createdate within the date range.
 * - Annual revenue sums across all companies (not just new ones) — treating it
 *   as a current snapshot of the CRM's total tracked revenue.
 */
function processCompanies(
  companies: ReadonlyArray<{ properties: Record<string, string | null> }>,
  startDate: Date,
  endDate: Date,
): { records: Record<string, unknown>[]; summaryDelta: Record<string, number> } {
  const byIndustry = new Map<string, number>();
  const byCountry = new Map<string, number>();
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  let companiesNew = 0;
  let annualRevenueSum = 0;

  for (const company of companies) {
    const props = company.properties ?? {};
    const ts = crmParseCreateDate(props.createdate);
    if (isFinite(ts) && ts >= startMs && ts <= endMs) {
      companiesNew++;
    }

    const industry = (props.industry ?? "").trim() || "unknown";
    byIndustry.set(industry, (byIndustry.get(industry) ?? 0) + 1);

    const country = (props.country ?? "").trim() || "unknown";
    byCountry.set(country, (byCountry.get(country) ?? 0) + 1);

    annualRevenueSum += crmParseStringNumber(props.annualrevenue);
  }

  const records: Record<string, unknown>[] = [];
  // Emit deterministic ordering: descending count, then key ascending
  const industrySorted = [...byIndustry.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  for (const [industry, count] of industrySorted) {
    records.push({
      _reportType: "companiesByIndustry",
      industry,
      companies_count: count,
      companies_by_industry: count,
    });
  }
  const countrySorted = [...byCountry.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  for (const [country, count] of countrySorted) {
    records.push({
      _reportType: "companiesByCountry",
      country,
      companies_count: count,
      companies_by_country: count,
    });
  }

  return {
    records,
    summaryDelta: {
      companies_total: companies.length,
      companies_new: companiesNew,
      companies_annual_revenue: Math.round(annualRevenueSum * 100) / 100,
    },
  };
}

/**
 * Build ticketsByStatus records + summary delta for tickets metrics.
 *
 * - tickets_total = unfiltered count. tickets_new = createdate-in-range count,
 *   computed from the `allTickets` list to avoid a second search API call
 *   (R2-M1: reclaim 1 req/cycle of our 4-req/s search quota).
 * - avg_time_to_first_response / avg_time_to_close are millisecond durations
 *   straight from HubSpot; we keep ms here so widget formatters can decide on
 *   display precision. Ignore nulls when averaging.
 */
function processTickets(
  allTickets: ReadonlyArray<{ properties: Record<string, string | null> }>,
  startDate: Date,
  endDate: Date,
): { records: Record<string, unknown>[]; summaryDelta: Record<string, number> } {
  const byStatus = new Map<string, number>();
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  let ticketsNew = 0;
  let responseSum = 0;
  let responseCount = 0;
  let closeSum = 0;
  let closeCount = 0;

  for (const ticket of allTickets) {
    const props = ticket.properties ?? {};
    const status = (props.hs_pipeline_stage ?? "").trim() || "unknown";
    byStatus.set(status, (byStatus.get(status) ?? 0) + 1);

    // Count "new" tickets in-memory from the full list's createdate property.
    const ts = crmParseCreateDate(props.createdate);
    if (isFinite(ts) && ts >= startMs && ts <= endMs) {
      ticketsNew++;
    }

    const firstResponse = props.time_to_first_response;
    if (firstResponse != null && firstResponse !== "") {
      const ms = parseFloat(firstResponse);
      if (isFinite(ms) && ms >= 0) {
        responseSum += ms;
        responseCount++;
      }
    }
    const closeMs = props.time_to_close;
    if (closeMs != null && closeMs !== "") {
      const ms = parseFloat(closeMs);
      if (isFinite(ms) && ms >= 0) {
        closeSum += ms;
        closeCount++;
      }
    }
  }

  const records: Record<string, unknown>[] = [];
  const sorted = [...byStatus.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  for (const [status, count] of sorted) {
    records.push({
      _reportType: "ticketsByStatus",
      ticket_status: status,
      tickets_count: count,
      tickets_by_status: count,
    });
  }

  return {
    records,
    summaryDelta: {
      tickets_total: allTickets.length,
      tickets_new: ticketsNew,
      avg_time_to_first_response:
        responseCount > 0 ? Math.round(responseSum / responseCount) : 0,
      avg_time_to_close: closeCount > 0 ? Math.round(closeSum / closeCount) : 0,
    },
  };
}

// ============================================================================
// Helper functions
// ============================================================================

function formatDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getDaysBetween(start: Date, end: Date): string[] {
  const days: string[] = [];
  // R3-H2: Use UTC methods consistently to avoid timezone-dependent off-by-one
  const current = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endUtc = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  while (current <= endUtc) {
    days.push(formatDateKey(current));
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return days;
}

/**
 * Classify deal stages into open/closed-won/closed-lost based on pipeline metadata.
 */
function classifyDealStages(
  pipelines: Array<{
    stages: Array<{
      id: string;
      label: string;
      metadata?: Record<string, string>;
    }>;
  }>,
): {
  closedWonStages: Set<string>;
  closedLostStages: Set<string>;
  openStages: Set<string>;
} {
  const closedWonStages = new Set<string>();
  const closedLostStages = new Set<string>();
  const openStages = new Set<string>();

  for (const pipeline of pipelines) {
    for (const stage of pipeline.stages) {
      const probability = stage.metadata?.probability;
      if (probability === CLOSED_WON_PROBABILITY) {
        closedWonStages.add(stage.id);
      } else if (probability === CLOSED_LOST_PROBABILITY) {
        closedLostStages.add(stage.id);
      } else {
        openStages.add(stage.id);
      }
    }
  }

  return { closedWonStages, closedLostStages, openStages };
}

/**
 * Story 25.6: Parse email aggregate stats into the summary record and generate
 * the emailDeviceBreakdown records.
 *
 * Returns a partial summary object and a list of device breakdown records.
 * Never throws; treats missing/invalid values as zero.
 */
function processEmailAggregate(
  aggregate:
    | {
        counters?: Record<string, number | null>;
        ratios?: Record<string, number | null>;
        deviceBreakdown?: {
          open_device_type?: Record<string, number | null>;
        };
      }
    | undefined,
): {
  summary: Record<string, number>;
  deviceRecords: Record<string, unknown>[];
} {
  const summary: Record<string, number> = {};

  // Counters
  const counters = aggregate?.counters ?? {};
  for (const counter of EMAIL_COUNTERS) {
    const raw = counters[counter];
    const value = typeof raw === "number" && isFinite(raw) ? raw : 0;
    summary[`email_${counter}`] = value;
  }

  // Ratios (stored as 0-1 decimals, rounded to 4 decimals)
  const ratios = aggregate?.ratios ?? {};
  for (const [apiKey, targetKey] of EMAIL_RATIO_KEYS) {
    const raw = ratios[apiKey];
    const value = typeof raw === "number" && isFinite(raw) ? raw : 0;
    // API may return 0-1 or 0-100 — inspect value range, normalize to 0-1
    const normalized = value > 1 ? value / 100 : value;
    summary[targetKey] = Math.round(normalized * 10000) / 10000;
  }

  // Device breakdown — generate 3 records (computer, mobile, unknown)
  const openDevice = aggregate?.deviceBreakdown?.open_device_type ?? {};
  const deviceRecords: Record<string, unknown>[] = [];
  let totalComputer = 0;
  let totalMobile = 0;
  let totalUnknown = 0;
  for (const device of EMAIL_DEVICE_TYPES) {
    const raw = openDevice[device];
    const count = typeof raw === "number" && isFinite(raw) ? raw : 0;
    deviceRecords.push({
      _reportType: "emailDeviceBreakdown",
      device_type: device,
      open_count: count,
    });
    if (device === "computer") totalComputer = count;
    else if (device === "mobile") totalMobile = count;
    else totalUnknown = count;
  }

  summary.email_open_computer = totalComputer;
  summary.email_open_mobile = totalMobile;
  summary.email_open_unknown = totalUnknown;

  return { summary, deviceRecords };
}

/**
 * Story 25.6: Build emailCampaigns records from /marketing/v3/emails objects.
 * Sorts descending by sent count.
 */
// Number-coercion helpers hoisted to module scope to avoid per-call closure allocation.
function emailToInt(raw: number | null | undefined): number {
  return typeof raw === "number" && isFinite(raw) ? raw : 0;
}

function emailToRate(raw: number | null | undefined): number {
  if (typeof raw !== "number" || !isFinite(raw)) return 0;
  const normalized = raw > 1 ? raw / 100 : raw;
  return Math.round(normalized * 10000) / 10000;
}

function emailDeriveRate(num: number, den: number): number {
  return den > 0 ? Math.round((num / den) * 10000) / 10000 : 0;
}

function buildEmailCampaignRecords(
  emails: MarketingEmailObject[],
): Record<string, unknown>[] {
  const records: Array<Record<string, unknown> & { sent: number }> = [];

  for (const email of emails) {
    const statsCounters = email.stats?.counters ?? {};
    const statsRatios = email.stats?.ratios ?? {};

    const sent = emailToInt(statsCounters.sent);
    const delivered = emailToInt(statsCounters.delivered);
    const opened = emailToInt(statsCounters.open);
    const clicked = emailToInt(statsCounters.click);
    const bounced = emailToInt(statsCounters.bounce ?? statsCounters.hardbounced);
    const unsubscribed = emailToInt(statsCounters.unsubscribed);

    // H3: Derive per-email open/click rates from delivered counter since per-email
    // objects rarely include the aggregate-level `ratios` field. Fall back to the
    // API-provided ratio only when `delivered` is zero (nothing to divide by).
    const openRate = delivered > 0
      ? emailDeriveRate(opened, delivered)
      : emailToRate(statsRatios.openratio);
    const clickRate = delivered > 0
      ? emailDeriveRate(clicked, delivered)
      : emailToRate(statsRatios.clickratio);

    records.push({
      _reportType: "emailCampaigns",
      email_name: email.name ?? "",
      email_subject: email.subject ?? "",
      email_type: email.type ?? "",
      email_state: email.state ?? "",
      send_date: email.publishDate ?? email.created ?? "",
      sent,
      delivered,
      opened,
      clicked,
      bounced,
      unsubscribed,
      // Per-email copies keyed for data-table column formatting
      email_campaign_sent: sent,
      email_campaign_open_rate: openRate,
      email_campaign_click_rate: clickRate,
    });
  }

  records.sort((a, b) => b.sent - a.sent);
  return records;
}

/**
 * Story 25.6: Aggregate email events into daily counts per event type.
 */
function buildDailyEmailEventRecords(
  eventsByType: Map<EmailEventType, { created: number }[]>,
): Record<string, unknown>[] {
  // R2-L1: Bucket in a nested map (date → eventType → count) instead of string keys,
  // so we never need to parse/split/escape separator characters.
  const perDate = new Map<string, Map<EmailEventType, number>>();

  // R3-M2: Max safe Date value is 8.64e15 ms (ECMA-262). Guard prevents RangeError
  // on malformed event payloads with extreme timestamps.
  const MAX_VALID_EPOCH_MS = 8.64e15;

  for (const [eventType, events] of eventsByType) {
    for (const event of events) {
      const ts = event.created;
      if (typeof ts !== "number" || !isFinite(ts) || ts <= 0 || ts > MAX_VALID_EPOCH_MS) continue;
      const date = new Date(ts).toISOString().slice(0, 10);
      let inner = perDate.get(date);
      if (!inner) {
        inner = new Map<EmailEventType, number>();
        perDate.set(date, inner);
      }
      inner.set(eventType, (inner.get(eventType) ?? 0) + 1);
    }
  }

  const records: Record<string, unknown>[] = [];
  const sortedDates = [...perDate.keys()].sort();
  for (const date of sortedDates) {
    const inner = perDate.get(date)!;
    for (const [eventType, count] of inner) {
      records.push({
        _reportType: "dailyEmailEvents",
        date,
        event_type: eventType,
        email_event_count: count,
      });
    }
  }
  return records;
}

// ============================================================================
// Story 25.10: Forms & Marketing Events constants + helpers
// ============================================================================

/** KV cache TTL for remembered scope-missing flags (24h). Shared across forms
 * and events — both use the same 1-day TTL so a misconfigured portal recovers
 * in the same window for either endpoint group. */
const FORMS_EVENTS_SCOPE_CACHE_TTL_SECONDS = 86400;

/** Zero-values for form summary fields (AC #1, #3). */
const FORMS_ZERO_SUMMARY: Record<string, number> = {
  total_form_submissions: 0,
};

/** Zero-values for event summary fields (AC #2, #3). */
const EVENTS_ZERO_SUMMARY: Record<string, number> = {
  total_event_registrations: 0,
  total_event_attendees: 0,
  event_attendance_rate: 0,
  total_cancellations: 0,
};

/** Coerce to finite non-negative integer; null/NaN/Infinity/negative → 0. */
function safeNonNegInt(raw: number | null | undefined): number {
  if (typeof raw !== "number" || !isFinite(raw) || raw < 0) return 0;
  return Math.round(raw);
}

/** Sleep helper for rate-limit pacing between form-submission batches. */
function formSubmissionSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Story 25.10: Build formSubmissions reportType records + aggregate summary.
 *
 * - `total_form_submissions` = sum of submission counts across all forms.
 * - One record per form with {form_name, form_id, submissions_count}.
 * - `form_conversion_rate` is null because /marketing/v3/forms does not expose
 *   form views (per Dev Notes "Form views are NOT available via API"). Surfacing
 *   the field as null lets widgets render "—" or a disclaimer rather than 0%.
 */
function processForms(
  forms: ReadonlyArray<HubSpotForm>,
  submissionCounts: ReadonlyArray<{ formGuid: string; count: number; truncated: boolean }>,
): {
  records: Record<string, unknown>[];
  summaryDelta: {
    total_form_submissions: number;
    forms_count: number;
    forms_partial_count: boolean;
    forms_archived_skipped: number;
  };
} {
  const countMap = new Map<string, { count: number; truncated: boolean }>();
  for (const entry of submissionCounts) {
    countMap.set(entry.formGuid, { count: entry.count, truncated: entry.truncated });
  }

  const records: Record<string, unknown>[] = [];
  let total = 0;
  let anyTruncated = false;
  let archivedSkipped = 0;

  for (const form of forms) {
    // R2-H1: Skip archived forms. HubSpot's /marketing/v3/forms list includes
    // forms with `archived=true` by default; agency dashboards want live forms
    // only. Counter `archived_forms_skipped` is surfaced on summary so widgets
    // can disclaim when present.
    if (form.archived === true) {
      archivedSkipped++;
      continue;
    }

    const formId = form.id;
    const formName = form.name?.trim() || formId;
    const data = countMap.get(formId) ?? { count: 0, truncated: false };
    const submissionsCount = safeNonNegInt(data.count);
    total += submissionsCount;
    if (data.truncated) anyTruncated = true;

    records.push({
      _reportType: "formSubmissions",
      form_name: formName,
      form_id: formId,
      // Emit both the registry key (form_submissions_count) AND the AC #1 short form
      // (submissions_count) so widgets binding to either find the same number. The
      // preset, palette, and sort path all use `form_submissions_count` as the
      // canonical key — the short form is a back-compat alias for the AC text.
      form_submissions_count: submissionsCount,
      submissions_count: submissionsCount,
      // AC #1: form_conversion_rate is null because form views aren't
      // available from /marketing/v3/forms. Widget formatter renders "—".
      form_conversion_rate: null,
      form_views: null,
    });
  }

  // Sort descending by form_submissions_count (preset expectation for data-table)
  records.sort(
    (a, b) =>
      (b.form_submissions_count as number) - (a.form_submissions_count as number),
  );

  return {
    records,
    summaryDelta: {
      total_form_submissions: total,
      // R2-H1: forms_count reflects ACTIVE (non-archived) forms — matches the
      // set of records actually emitted. Archived count tracked separately.
      forms_count: forms.length - archivedSkipped,
      forms_partial_count: anyTruncated,
      forms_archived_skipped: archivedSkipped,
    },
  };
}

/**
 * Story 25.10: Build eventPerformance reportType records + aggregate summary.
 *
 * Counters come embedded on the list response (HubSpot 2026-01). Attendance
 * rate = attended / registered, stored as 0-1 decimal (widget formatter
 * multiplies by 100 for %).
 *
 * Event status is derived from eventStatus string first, then the
 * eventCompleted/eventCancelled booleans as fallback, then "scheduled" by default.
 *
 * **Summary contract (R3-L1):**
 * - `events_count` — events that survived the date-range filter (if any); matches
 *   the count of emitted `eventPerformance` records.
 * - `events_filtered_out_of_range` — events whose `startDateTime` fell outside
 *   the requested period. Widgets should show a disclaimer like
 *   "N earlier/later events not shown" when this is > 0.
 * - `total_event_registrations`/`_attendees`/`_cancellations` only sum the
 *   in-period events (matches `events_count`, not `events.length`).
 */
function processMarketingEvents(
  events: ReadonlyArray<HubSpotMarketingEvent>,
  dateRange?: { start: Date; end: Date },
): {
  records: Record<string, unknown>[];
  summaryDelta: {
    total_event_registrations: number;
    total_event_attendees: number;
    event_attendance_rate: number;
    total_cancellations: number;
    events_count: number;
    events_filtered_out_of_range: number;
  };
} {
  const records: Record<string, unknown>[] = [];
  let totalReg = 0;
  let totalAttended = 0;
  let totalCancelled = 0;
  let filteredOut = 0;

  // R2-M3: Filter events that START outside the requested date range, if provided.
  // HubSpot's /marketing/v3/marketing-events list endpoint has no server-side
  // date filter, so we filter client-side. Events with no startDateTime pass
  // through (conservative — unknown start could be in range).
  const startMs = dateRange?.start.getTime();
  const endMs = dateRange?.end.getTime();

  for (const event of events) {
    if (startMs != null && endMs != null && event.startDateTime) {
      const eventStartMs = Date.parse(event.startDateTime);
      if (isFinite(eventStartMs) && (eventStartMs < startMs || eventStartMs > endMs)) {
        filteredOut++;
        continue;
      }
    }
    const registered = safeNonNegInt(event.registrants);
    const attended = safeNonNegInt(event.attendees);
    const cancelled = safeNonNegInt(event.cancellations);

    totalReg += registered;
    totalAttended += attended;
    totalCancelled += cancelled;

    const attendanceRate = registered > 0
      ? Math.round((attended / registered) * 10000) / 10000
      : 0;

    // R1-L1: eventId falls back to "" if both objectId and externalEventId are
    // absent. In practice HubSpot v3 always returns objectId; the fallback is
    // purely defensive against malformed payloads and keeps the record emittable.
    const eventId = event.objectId ?? event.externalEventId ?? "";
    const eventName = event.eventName?.trim() || eventId || "(unnamed event)";
    const status = deriveEventStatus(event);

    records.push({
      _reportType: "eventPerformance",
      event_name: eventName,
      event_id: eventId,
      event_status: status,
      registrations: registered,
      attended,
      cancelled,
      attendance_rate: attendanceRate,
    });
  }

  // Sort descending by registrations to match Event Attendance preset expectations
  records.sort(
    (a, b) => (b.registrations as number) - (a.registrations as number),
  );

  const overallAttendanceRate = totalReg > 0
    ? Math.round((totalAttended / totalReg) * 10000) / 10000
    : 0;

  return {
    records,
    summaryDelta: {
      total_event_registrations: totalReg,
      total_event_attendees: totalAttended,
      event_attendance_rate: overallAttendanceRate,
      total_cancellations: totalCancelled,
      // events_count = events that survived the date-range filter. Events filtered
      // out are NOT included here — the summary reflects what the period shows.
      events_count: events.length - filteredOut,
      events_filtered_out_of_range: filteredOut,
    },
  };
}

/**
 * Derive a normalized event status string. HubSpot's v3 response surfaces:
 *   - `eventStatus`: explicit enum ("CANCELLED", "COMPLETED", "SCHEDULED", ...).
 *   - `eventCompleted` / `eventCancelled`: boolean fallbacks on older portals.
 * Missing fields fall back to "scheduled" for display purposes.
 */
function deriveEventStatus(event: HubSpotMarketingEvent): string {
  const raw = event.eventStatus?.trim();
  if (raw) return raw.toLowerCase();
  if (event.eventCancelled === true) return "cancelled";
  if (event.eventCompleted === true) return "completed";
  return "scheduled";
}

// ============================================================================
// Story 25.11: Workflows + Custom Events + Remaining CRM constants/helpers
// ============================================================================

/**
 * KV cache TTL for remembered workflow/custom-events scope-missing flags (24h).
 *
 * R1-M2: Recovery path is TTL-only — if a portal grants the optional scope
 * after we've cached `scope_missing`, the adapter waits up to 24h before
 * re-probing. This matches the pattern used by email/traffic/campaigns scopes
 * (Stories 25.6-25.8). Explicit re-auth detection (cache-key invalidation on
 * OAuth callback) is a future-story enhancement; for now the 24h TTL keeps the
 * KV key-count bounded and re-authorizations recover within a day.
 */
const WORKFLOWS_SCOPE_CACHE_TTL_SECONDS = 86400;
const CUSTOM_EVENTS_SCOPE_CACHE_TTL_SECONDS = 86400;

/** Zero-value summary fields for workflows (graceful skip default). */
const WORKFLOWS_ZERO_SUMMARY: Record<string, number> = {
  total_workflow_enrollments: 0,
  active_enrollments: 0,
  completed_enrollments: 0,
  workflow_goal_conversion_rate: 0,
  avg_time_in_workflow: 0,
};

/** Zero-value summary fields for custom events. */
const CUSTOM_EVENTS_ZERO_SUMMARY: Record<string, number> = {
  custom_event_count: 0,
};

/** Top-N buckets for email domain distribution + "other" tail (AC #3). */
const EMAIL_DOMAIN_TOP_N = 10;
/** Top-N buckets for city distribution (AC #5). */
const CITY_TOP_N = 20;

/** Parse the domain part of an RFC 5322-lite email address: lowercase, trim
 * whitespace, take last "@" segment. Returns null when the value is missing
 * or clearly malformed.
 *
 * R1-L2: We use `lastIndexOf("@")` deliberately. RFC 5322 allows quoted
 * local parts containing literal `@` (e.g. `"foo@bar"@example.com`), and for
 * those valid-but-rare addresses we want the canonical registered domain
 * (`example.com`), not `bar"@example.com`. For invalid multi-`@` addresses
 * (`a@b@c`), taking the last `@` still produces the most domain-like suffix.
 */
function parseEmailDomain(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  const atIdx = trimmed.lastIndexOf("@");
  if (atIdx < 0 || atIdx === trimmed.length - 1) return null;
  const domain = trimmed.slice(atIdx + 1).trim();
  // Domain must contain at least one dot and no whitespace.
  if (!domain || domain.includes(" ") || !domain.includes(".")) return null;
  return domain;
}

/**
 * Bucket a string value with top-N + "other" rollup. Returns the canonical
 * output keys sorted descending by count. Empty/null inputs bucket into
 * `unknownLabel` so we never silently drop data (AC: #5 geography dev note).
 *
 * R1-H1: Pass a non-finite `limit` (e.g. Number.POSITIVE_INFINITY) to disable
 * rollup entirely; the function short-circuits before attempting the top-N
 * split so the "Other" bucket branch isn't dead code — it's genuinely bypassed.
 */
function bucketTopN(
  items: ReadonlyArray<string | null | undefined>,
  limit: number,
  unknownLabel = "Unknown",
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const raw of items) {
    const value = raw == null ? "" : String(raw).trim();
    const key = value || unknownLabel;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  // R1-H1: Explicit early return for the "no rollup" case makes intent clear.
  if (!Number.isFinite(limit) || counts.size <= limit) return counts;

  const sorted = [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  const top = sorted.slice(0, limit);
  let otherCount = 0;
  for (let i = limit; i < sorted.length; i++) otherCount += sorted[i]![1];

  const result = new Map<string, number>(top);
  if (otherCount > 0) result.set("Other", otherCount);
  return result;
}

/**
 * Story 25.11 (Task 3): Build contactsByEmailDomain records + summary deltas
 * for #79/#80 contact-level metrics. Caller supplies the full contact list
 * (typically the date-range search) so `avg_days_to_close_contact` aggregates
 * only period-new contacts.
 */
function processContactExtras(
  contacts: ReadonlyArray<{ properties: Record<string, string | null> }>,
): {
  emailDomainRecords: Record<string, unknown>[];
  geographyRecords: Record<string, unknown>[];
  // R1-H2: avg_days_to_close_contact is nullable — `null` signals "no data" so
  // widgets can render "—" instead of a misleading 0. Zero is a legitimate
  // value for portals where all contacts closed same-day, so the no-data case
  // must be distinguishable.
  summaryDelta: Record<string, number | null>;
} {
  const emails: Array<string | null> = [];
  const countries: Array<string | null> = [];
  const cities: Array<string | null> = [];
  let daysToCloseSum = 0;
  let daysToCloseCount = 0;

  for (const contact of contacts) {
    const props = contact.properties ?? {};
    emails.push(parseEmailDomain(props.email));
    countries.push(props.country ?? null);
    cities.push(props.city ?? null);

    const raw = props.days_to_close;
    if (raw != null && raw !== "") {
      const n = parseFloat(raw);
      if (isFinite(n) && n >= 0 && n <= 3650) {
        daysToCloseSum += n;
        daysToCloseCount++;
      }
    }
  }

  // R2-M1: Email domain bucketing — include nulls as "unknown" so the total
  // matches contacts.length. Widgets can filter "unknown" out if desired.
  const domainCounts = bucketTopN(emails, EMAIL_DOMAIN_TOP_N, "unknown");
  const emailDomainRecords: Record<string, unknown>[] = [];
  const sortedDomains = [...domainCounts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  for (const [domain, count] of sortedDomains) {
    emailDomainRecords.push({
      _reportType: "contactsByEmailDomain",
      email_domain: domain,
      contacts_count: count,
      contacts_by_email_domain: count,
    });
  }

  const countryCounts = bucketTopN(countries, Number.POSITIVE_INFINITY, "Unknown");
  const cityCounts = bucketTopN(cities, CITY_TOP_N, "Unknown");
  const geographyRecords: Record<string, unknown>[] = [];
  const sortedCountries = [...countryCounts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  for (const [country, count] of sortedCountries) {
    geographyRecords.push({
      _reportType: "contactsByCountry",
      country,
      contacts_count: count,
      contacts_by_country: count,
    });
  }
  const sortedCities = [...cityCounts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  for (const [city, count] of sortedCities) {
    geographyRecords.push({
      _reportType: "contactsByCity",
      city,
      contacts_count: count,
      contacts_by_city: count,
    });
  }

  // R1-H2: Return null when no contacts have days_to_close populated. Zero is
  // a valid measurement (same-day close) so it must not overlap with no-data.
  const avgDaysToCloseContact =
    daysToCloseCount > 0
      ? Math.round((daysToCloseSum / daysToCloseCount) * 100) / 100
      : null;

  return {
    emailDomainRecords,
    geographyRecords,
    summaryDelta: {
      avg_days_to_close_contact: avgDaysToCloseContact,
    },
  };
}

/**
 * Story 25.11 (Task 4): Build dealForecast records from the merged deal set.
 * Emits one record per deal with a non-null `hs_forecast_probability`. The
 * forecast_category dimension is derived from the numeric probability to
 * remain compatible with portals that haven't populated `hs_forecast_category`.
 *
 * Probability is stored as 0-1 decimal (per HubSpot); widget renderers
 * multiply for percentage display.
 */
function processDealForecast(
  deals: ReadonlyArray<{ id: string; properties: Record<string, string | null> }>,
  stageMap: Map<string, string>,
): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = [];
  for (const deal of deals) {
    const raw = deal.properties.hs_forecast_probability;
    if (raw == null || raw === "") continue;
    const prob = parseFloat(raw);
    // R3-H1: Use Number.isFinite — spec-level check, not affected by implicit
    // coercion. isFinite("1") === true (coerces); Number.isFinite("1") === false.
    if (!Number.isFinite(prob) || prob < 0 || prob > 1) continue;

    // R3-H2: Empty-string stageId defaults to "unknown" label (the `??` chain
    // didn't trigger because "" is not nullish — explicit check needed).
    const stageId = deal.properties.dealstage ?? "";
    const stageLabel =
      (stageId && stageMap.get(stageId)) || stageId || "unknown";
    const amount = parseFloat(deal.properties.amount ?? "0") || 0;
    const forecastCategory =
      deal.properties.hs_forecast_category?.trim().toLowerCase() ||
      deriveForecastCategory(prob);

    records.push({
      _reportType: "dealForecast",
      deal_id: deal.id,
      deal_stage: stageLabel,
      forecast_category: forecastCategory,
      // R3-M5: Emit both per-record alias (`forecast_probability`) and the
      // registry-canonical key (`deal_forecast_probability`). Widgets bound to
      // either form resolve to the same value. Data-table column configs use
      // the canonical key; preset `metricKeys` references the canonical key
      // so the sort-by / column-order both work deterministically.
      forecast_probability: Math.round(prob * 10000) / 10000,
      deal_forecast_probability: Math.round(prob * 10000) / 10000,
      amount: Math.round(amount * 100) / 100,
    });
  }
  // Sort descending by amount so the top deals land at the top of data-tables.
  records.sort((a, b) => (b.amount as number) - (a.amount as number));
  return records;
}

/**
 * Derive a forecast category bucket from a probability value. HubSpot's
 * `hs_forecast_category` enum is `omit` / `pipeline` / `bestcase` / `commit` /
 * `closed` — we emit lowercase labels matching the vendor. Deals without a
 * populated category fall into these derived buckets based on probability.
 *
 * R1-M1: Threshold 0.7 for "commit" matches HubSpot's canonical default
 * (docs: "probability of closing ≥ 70% → forecast_category=commit"). Prior
 * threshold 0.9 was too conservative — most sales teams commit at 70%+.
 */
function deriveForecastCategory(prob: number): string {
  if (prob <= 0) return "omit";
  if (prob >= 1) return "closed";
  if (prob >= 0.7) return "commit";
  if (prob >= 0.4) return "bestcase";
  return "pipeline";
}

/**
 * Story 25.11 (Task 1): Process workflow list + per-workflow stats into
 * workflowPerformance records + summary aggregate deltas.
 *
 * - Emits one record per workflow with `enrollments`, `active`, `completed`,
 *   `conversion_rate` (0-1 decimal) fields.
 * - Summary aggregates sum counters across workflows. Goal conversion rate is
 *   the workflow-weighted mean (sum(completed) / sum(total)).
 * - Avg time in workflow is the simple mean of per-workflow avg values
 *   (weighting by enrollments would need per-flow enrollment arrays we don't
 *   fetch). Null values are ignored in the mean.
 */
function processWorkflows(
  workflows: ReadonlyArray<HubSpotWorkflow>,
  statsByFlowId: ReadonlyMap<string, HubSpotWorkflowStats>,
): {
  records: Record<string, unknown>[];
  // R1-H3: `workflow_stats_available` is a boolean flag; the rest are numeric
  // aggregates. Typed as `Record<string, number | boolean>` to encode both.
  summaryDelta: Record<string, number | boolean>;
} {
  const records: Record<string, unknown>[] = [];
  let totalEnrollments = 0;
  let totalActive = 0;
  let totalCompleted = 0;
  let avgTimeSum = 0;
  let avgTimeCount = 0;

  // R1-H3: Track whether at least one workflow returned usable stats from
  // Automation v4 BETA — surfaces as summary flag so widgets can disclaim
  // zero rows as "no stats available" vs "no enrollments yet".
  let anyStatsAvailable = false;

  for (const wf of workflows) {
    const stats = statsByFlowId.get(wf.id);
    const hasStats =
      stats != null &&
      (stats.total_enrollments !== null ||
        stats.active_enrollments !== null ||
        stats.completed_enrollments !== null);
    if (hasStats) anyStatsAvailable = true;
    const total = stats?.total_enrollments ?? 0;
    const active = stats?.active_enrollments ?? 0;
    const completed = stats?.completed_enrollments ?? 0;
    const avgTimeMs = stats?.avg_time_in_workflow_ms;
    const conversionRate =
      total > 0 ? Math.round((completed / total) * 10000) / 10000 : 0;

    totalEnrollments += total;
    totalActive += active;
    totalCompleted += completed;
    if (typeof avgTimeMs === "number" && Number.isFinite(avgTimeMs) && avgTimeMs >= 0) {
      avgTimeSum += avgTimeMs;
      avgTimeCount++;
    }

    const name = wf.name?.trim() || wf.id;
    records.push({
      _reportType: "workflowPerformance",
      workflow_id: wf.id,
      workflow_name: name,
      enrollments: total,
      active,
      completed,
      conversion_rate: conversionRate,
      // R1-H3: Per-record flag lets data-table widgets render "—" for rows
      // where Automation v4 BETA didn't return stats vs showing a misleading 0.
      workflow_stats_available: hasStats,
      total_workflow_enrollments: total,
      active_enrollments: active,
      completed_enrollments: completed,
      workflow_goal_conversion_rate: conversionRate,
    });
  }

  records.sort(
    (a, b) => (b.enrollments as number) - (a.enrollments as number),
  );

  const aggregateConversionRate =
    totalEnrollments > 0
      ? Math.round((totalCompleted / totalEnrollments) * 10000) / 10000
      : 0;
  const avgTimeInWorkflow =
    avgTimeCount > 0 ? Math.round(avgTimeSum / avgTimeCount) : 0;

  return {
    records,
    summaryDelta: {
      total_workflow_enrollments: totalEnrollments,
      active_enrollments: totalActive,
      completed_enrollments: totalCompleted,
      workflow_goal_conversion_rate: aggregateConversionRate,
      avg_time_in_workflow: avgTimeInWorkflow,
      // R1-H3: Summary flag for widgets to disclaim missing BETA API data.
      workflow_stats_available: anyStatsAvailable,
    },
  };
}

/**
 * Sleep helper for workflow + custom-events batch pacing.
 * R2-L2: Single helper shared by both batch loops since they use the same
 * rate-limit budget (100 req/10s OAuth cap). The earlier `campaignSleep` /
 * `formSubmissionSleep` helpers are kept as-is for historical consistency
 * with their stories — new Story 25.11 batches use this shared helper.
 */
function hubspotBatchSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ============================================================================
// Adapter Implementation
// ============================================================================

export const hubspotAdapter: IntegrationAdapter = {
  platform: "hubspot",
  rateLimits: { requestsPerMinute: 660, retryAfterMs: 1000 },

  getAvailableMetrics(): MetricDefinition[] {
    return HUBSPOT_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return HUBSPOT_DIMENSIONS;
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date },
  ): Promise<AdapterResult<AdapterMetrics>> {
    const kv = ctx.env.KV as KVStore;
    const { accessToken } = credentials;
    const { tenantId, integrationId, accountId } = ctx;

    // 1. Fetch pipelines + stages + owners (cached 24h, M4: keyed by accountId)
    const [pipelinesResult, ownersResult] = await Promise.all([
      fetchPipelines(kv, accessToken, tenantId, integrationId, accountId),
      fetchOwners(kv, accessToken, tenantId, integrationId, accountId),
    ]);
    if (!pipelinesResult.success) return pipelinesResult;
    if (!ownersResult.success) return ownersResult;

    const ownerMap = ownersResult.data;

    const { closedWonStages, closedLostStages } =
      classifyDealStages(pipelinesResult.data);

    // Build stage + pipeline maps in a single pass (R3-L1)
    const stageMap = new Map<string, string>();
    const stageInfoMap = new Map<string, { label: string; metadata?: Record<string, string> }>();
    const pipelineMap = new Map<string, string>();
    for (const pipeline of pipelinesResult.data) {
      pipelineMap.set(pipeline.id, pipeline.label);
      for (const stage of pipeline.stages) {
        stageMap.set(stage.id, stage.label);
        stageInfoMap.set(stage.id, { label: stage.label, metadata: stage.metadata });
      }
    }

    // 2. Fetch contacts and deals in parallel (H3: independent calls)
    // Story 25.3: Two deal searches — by createdate AND closedate — for accurate revenue attribution
    const dealProperties = [
      "amount",
      "dealstage",
      "pipeline",
      "createdate",
      "closedate",
      "dealtype",
      "hubspot_owner_id",
      "hs_forecast_probability",
      // Story 25.11 (Task 4): forecast_category dimension — optional HubSpot
      // enum; adapter derives it from probability when unset.
      "hs_forecast_category",
    ];

    const [contactsResult, dealsCreatedResult, dealsClosedResult, totalContactsResult, totalDealsResult] = await Promise.all([
      // New contacts in date range (by createdate)
      // Story 25.11: added `email`, `days_to_close`, `country`, `state`, `city`
      // for email-domain distribution, avg days-to-close, and geography dims.
      searchCrmObjects(
        accessToken,
        "contacts",
        ["lifecyclestage", "createdate", "hs_analytics_source", "hubspotscore", "hs_analytics_source_data_1", "hs_analytics_source_data_2", "hs_latest_source", "hs_latest_source_data_1", "hs_latest_source_data_2", "email", "days_to_close", "country", "state", "city"],
        "createdate",
        dateRange.start,
        dateRange.end,
      ),
      // Deals created in date range (by createdate)
      searchCrmObjects(accessToken, "deals", dealProperties, "createdate", dateRange.start, dateRange.end),
      // Deals closed in date range (by closedate) — captures deals created before period but closed within it
      searchCrmObjects(accessToken, "deals", dealProperties, "closedate", dateRange.start, dateRange.end),
      // Total contacts (unfiltered, best-effort)
      fetchCrmObjects(accessToken, "contacts", ["createdate"]),
      // Total deals (unfiltered, best-effort)
      fetchCrmObjects(accessToken, "deals", ["dealstage"]),
    ]);
    if (!contactsResult.success) return contactsResult;
    if (!dealsCreatedResult.success) return dealsCreatedResult;
    if (!dealsClosedResult.success) return dealsClosedResult;

    // Story 25.3: Calculate revenue_by_closedate from closedate-filtered set ONLY (before merging)
    const closedateDeals = dealsClosedResult.data;
    let revenueByClosedate = 0;
    for (const deal of closedateDeals) {
      const stageId = deal.properties.dealstage ?? "";
      if (closedWonStages.has(stageId)) {
        revenueByClosedate += parseFloat(deal.properties.amount ?? "0") || 0;
      }
    }
    revenueByClosedate = Math.round(revenueByClosedate * 100) / 100;

    // Story 25.3: First-write-wins deduplication — createdate results take priority
    const mergedDeals = new Map<string, typeof dealsCreatedResult.data[number]>();
    for (const deal of dealsCreatedResult.data) {
      mergedDeals.set(deal.id, deal);
    }
    for (const deal of closedateDeals) {
      if (!mergedDeals.has(deal.id)) {
        mergedDeals.set(deal.id, deal);
      }
    }

    // Story 25.3: Truncation detection — true when either search hit MAX_PAGES * SEARCH_PAGE_SIZE
    const truncationThreshold = MAX_PAGES * SEARCH_PAGE_SIZE;
    const isTruncated =
      dealsCreatedResult.data.length >= truncationThreshold ||
      closedateDeals.length >= truncationThreshold;

    // Total counts are best-effort — fallback to date-filtered count if unfiltered fetch fails
    const totalContacts = totalContactsResult.success
      ? totalContactsResult.data.length
      : contactsResult.data.length;
    const totalDealsCount = totalDealsResult.success
      ? totalDealsResult.data.length
      : dealsCreatedResult.data.length;

    // 4. Aggregate contact metrics
    const contacts = contactsResult.data;
    let subscriberCount = 0;
    let leadCount = 0;
    let mqlCount = 0;
    let sqlCount = 0;
    let opportunityCount = 0;
    let customerCount = 0;

    // Contact score accumulation (skip nulls)
    let scoreSum = 0;
    let scoreCount = 0;

    // Grouping maps for contactsBySource and contactsByLifecycle
    const contactsBySource = new Map<string, number>();
    const contactsByLifecycle = new Map<string, number>();

    // Story 25.5: Attribution grouping maps
    const contactsBySourceDetail = new Map<string, { source: string; drilldown1: string; count: number }>();
    const contactsByLatestSource = new Map<string, number>();
    const dailyContactsBySource = new Map<string, number>();

    // Daily contact counts
    const dailyContacts = new Map<string, number>();

    // Named stage counters track the 6 standard HubSpot lifecycle stages for the summary record.
    // Custom/non-standard stages (e.g., "evangelist") are captured in contactsByLifecycle but not
    // in individual summary counters — the sum of named counters may be less than total contacts.
    for (const contact of contacts) {
      const stage = contact.properties.lifecyclestage?.toLowerCase();
      if (stage === SUBSCRIBER_STAGE) subscriberCount++;
      else if (stage === LEAD_STAGE) leadCount++;
      else if (stage === MQL_STAGE) mqlCount++;
      else if (stage === SQL_STAGE) sqlCount++;
      else if (stage === OPPORTUNITY_STAGE) opportunityCount++;
      else if (stage === CUSTOMER_STAGE) customerCount++;

      // Accumulate hubspotscore (skip nulls)
      const rawScore = contact.properties.hubspotscore;
      if (rawScore != null && rawScore !== "") {
        const score = parseFloat(rawScore);
        if (!isNaN(score)) {
          scoreSum += score;
          scoreCount++;
        }
      }

      // Group by source
      const source = normalizeSource(contact.properties.hs_analytics_source);
      contactsBySource.set(source, (contactsBySource.get(source) ?? 0) + 1);

      // Group by lifecycle stage (all stages dynamically)
      const lifecycleKey = stage || "unknown";
      contactsByLifecycle.set(lifecycleKey, (contactsByLifecycle.get(lifecycleKey) ?? 0) + 1);

      // Story 25.5: Source detail grouping (first-touch with drill-down 1)
      const rawDd1 = contact.properties.hs_analytics_source_data_1;
      const drilldown1 = rawDd1?.toLowerCase().trim() || source; // fallback to source label
      const sdKey = `${source}::${drilldown1}`;
      const sdEntry = contactsBySourceDetail.get(sdKey);
      if (sdEntry) {
        sdEntry.count++;
      } else {
        contactsBySourceDetail.set(sdKey, { source, drilldown1, count: 1 });
      }

      // Story 25.5: Latest source grouping (last-touch attribution)
      const latestSource = normalizeSource(contact.properties.hs_latest_source);
      contactsByLatestSource.set(latestSource, (contactsByLatestSource.get(latestSource) ?? 0) + 1);

      const createDate = contact.properties.createdate;
      if (createDate) {
        const dayKey = createDate.slice(0, 10);
        dailyContacts.set(dayKey, (dailyContacts.get(dayKey) ?? 0) + 1);

        // Story 25.5: Daily contacts by source (for attribution trend)
        const dcsKey = `${dayKey}::${source}`;
        dailyContactsBySource.set(dcsKey, (dailyContactsBySource.get(dcsKey) ?? 0) + 1);
      }
    }

    // Derived: avg contact score (null if no scored contacts)
    const avgContactScore = scoreCount > 0 ? Math.round(scoreSum / scoreCount) : null;

    // 5. Aggregate deal metrics from MERGED deal set
    const deals = [...mergedDeals.values()];
    let closedWon = 0;
    let closedLost = 0;
    let pipelineValue = 0;
    let totalDealAmount = 0;

    // Daily deal counts
    const dailyDeals = new Map<string, number>();
    const dailyRevenue = new Map<string, number>();

    // Deals by stage for funnel
    const dealsByStage = new Map<string, { count: number; amount: number }>();

    // Story 25.3: New grouping maps
    const dealsByPipeline = new Map<string, { pipeline_name: string; deals_count: number; total_amount: number }>();
    const dealsByOwner = new Map<string, { owner_name: string; deals_count: number; total_amount: number }>();
    const dealsByType = new Map<string, { deal_type: string; deals_count: number }>();
    let unassignedDealsCount = 0;

    // Story 25.3: Weighted pipeline value + avg days to close
    let weightedPipelineValue = 0;
    const closedWonDaysToClose: number[] = [];
    const dealAmounts: number[] = [];

    for (const deal of deals) {
      const stageId = deal.properties.dealstage ?? "";
      const amount = parseFloat(deal.properties.amount ?? "0") || 0;

      // Classify by stage
      if (closedWonStages.has(stageId)) {
        closedWon++;
      } else if (closedLostStages.has(stageId)) {
        closedLost++;
      } else {
        pipelineValue += amount;
      }

      totalDealAmount += amount;

      // Collect positive amounts for largest_deal_amount
      if (amount > 0) {
        dealAmounts.push(amount);
      }

      // Deals by stage aggregation
      const stageLabel = stageMap.get(stageId) ?? stageId;
      const existing = dealsByStage.get(stageLabel) ?? {
        count: 0,
        amount: 0,
      };
      dealsByStage.set(stageLabel, {
        count: existing.count + 1,
        amount: existing.amount + amount,
      });

      // Story 25.3: Deals by pipeline grouping
      const pipelineId = deal.properties.pipeline ?? "";
      const pipelineName = pipelineMap.get(pipelineId) ?? pipelineId;
      const pipelineEntry = dealsByPipeline.get(pipelineId) ?? { pipeline_name: pipelineName, deals_count: 0, total_amount: 0 };
      pipelineEntry.deals_count++;
      pipelineEntry.total_amount += amount;
      dealsByPipeline.set(pipelineId, pipelineEntry);

      // Story 25.3: Deals by owner grouping (exclude unassigned)
      const ownerId = deal.properties.hubspot_owner_id;
      if (ownerId != null && ownerId !== "") {
        const ownerName = ownerMap.get(ownerId) ?? "Unknown Owner";
        const ownerEntry = dealsByOwner.get(ownerId) ?? { owner_name: ownerName, deals_count: 0, total_amount: 0 };
        ownerEntry.deals_count++;
        ownerEntry.total_amount += amount;
        dealsByOwner.set(ownerId, ownerEntry);
      } else {
        unassignedDealsCount++;
      }

      // Story 25.3: Deals by type grouping
      const dealType = (deal.properties.dealtype ?? "unknown").toLowerCase().trim() || "unknown";
      const typeEntry = dealsByType.get(dealType) ?? { deal_type: dealType, deals_count: 0 };
      typeEntry.deals_count++;
      dealsByType.set(dealType, typeEntry);

      // Story 25.3: Weighted pipeline value — open deals only (0 < probability < 1)
      const stageInfo = stageInfoMap.get(stageId);
      const prob = parseFloat(stageInfo?.metadata?.probability ?? "0");
      if (!isNaN(prob) && prob > 0 && prob < 1) {
        weightedPipelineValue += amount * prob;
      }

      // Story 25.3: Avg days to close — closed-won deals with valid dates
      if (closedWonStages.has(stageId)) {
        const closeDate = deal.properties.closedate;
        const createDate = deal.properties.createdate;
        if (closeDate && createDate) {
          const closeMs = new Date(closeDate).getTime();
          const createMs = new Date(createDate).getTime();
          const days = (closeMs - createMs) / 86400000;
          if (!isNaN(days) && days >= 0 && days <= 3650) {
            closedWonDaysToClose.push(days);
          }
        }
      }

      // Daily aggregation — deals_new by createdate, revenue by closedate (H4)
      const dealCreateDate = deal.properties.createdate;
      if (dealCreateDate) {
        const dayKey = dealCreateDate.slice(0, 10);
        dailyDeals.set(dayKey, (dailyDeals.get(dayKey) ?? 0) + 1);
      }

      // Revenue bucketed by closedate (when deal was actually closed)
      if (closedWonStages.has(stageId)) {
        const closeDate = deal.properties.closedate;
        if (closeDate) {
          const closeDayKey = closeDate.slice(0, 10);
          dailyRevenue.set(closeDayKey, (dailyRevenue.get(closeDayKey) ?? 0) + amount);
        }
      }
    }

    // 6. Derived metrics
    const totalClosed = closedWon + closedLost;
    const winRate = totalClosed > 0 ? closedWon / totalClosed : 0;
    const avgDealAmount = deals.length > 0 ? totalDealAmount / deals.length : 0;

    // Story 25.3: Avg days to close (null if no valid closed-won deals)
    const avgDaysToClose = closedWonDaysToClose.length > 0
      ? Math.round((closedWonDaysToClose.reduce((sum, d) => sum + d, 0) / closedWonDaysToClose.length) * 100) / 100
      : null;

    // Story 25.3: Largest deal amount (null if no deals with amount > 0)
    let largestDealAmount: number | null = null;
    if (dealAmounts.length > 0) {
      // Use loop instead of Math.max(...arr) to avoid stack overflow for >10K deals
      let max = dealAmounts[0]!;
      for (let i = 1; i < dealAmounts.length; i++) {
        if (dealAmounts[i]! > max) max = dealAmounts[i]!;
      }
      largestDealAmount = max;
    }

    // 7. Build records
    const records: Record<string, unknown>[] = [];

    // Summary record (H1: contacts_total != contacts_new)
    records.push({
      _reportType: "summary",
      contacts_total: totalContacts,
      contacts_new: contacts.length,
      subscriber_count: subscriberCount,
      lead_count: leadCount,
      mql_count: mqlCount,
      sql_count: sqlCount,
      opportunity_count: opportunityCount,
      customer_count: customerCount,
      avg_contact_score: avgContactScore,
      deals_total: totalDealsCount,
      deals_new: dealsCreatedResult.data.length, // R1-H3: count createdate deals only — merged set is for groupings
      deals_closed_won: closedWon,
      deals_closed_lost: closedLost,
      win_rate: Math.round(winRate * 10000) / 10000, // L1: 0-1 decimal — widget formatter multiplies by 100 for display
      pipeline_value: Math.round(pipelineValue * 100) / 100,
      revenue_closed_won: revenueByClosedate, // Story 25.3: backward compat — set to closedate-based revenue
      avg_deal_amount: Math.round(avgDealAmount * 100) / 100,
      // Story 25.3: New summary fields
      weighted_pipeline_value: Math.round(weightedPipelineValue * 100) / 100,
      avg_days_to_close: avgDaysToClose,
      largest_deal_amount: largestDealAmount,
      unassigned_deals_count: unassignedDealsCount,
      revenue_by_closedate: revenueByClosedate,
      _truncated: isTruncated,
    });

    // Daily overview records
    const allDays = getDaysBetween(dateRange.start, dateRange.end);
    for (const day of allDays) {
      records.push({
        _reportType: "dailyOverview",
        date: day,
        contacts_new: dailyContacts.get(day) ?? 0,
        deals_new: dailyDeals.get(day) ?? 0,
        revenue_closed_won: dailyRevenue.get(day) ?? 0,
      });
    }

    // Deals by stage records (for funnel widget)
    for (const [stageName, data] of dealsByStage) {
      records.push({
        _reportType: "dealsByStage",
        deal_stage: stageName,
        deals_by_stage: data.count,
        pipeline_value: Math.round(data.amount * 100) / 100,
      });
    }

    // Contacts by source records (Story 25.2, AC #2)
    for (const [source, count] of contactsBySource) {
      records.push({
        _reportType: "contactsBySource",
        source,
        contacts_count: count,
      });
    }

    // Contacts by lifecycle stage records (Story 25.2, AC #3)
    for (const [stage, count] of contactsByLifecycle) {
      records.push({
        _reportType: "contactsByLifecycle",
        lifecycle_stage: stage,
        contacts_count: count,
      });
    }

    // Story 25.3: Deals by pipeline records (AC #2)
    for (const [, data] of dealsByPipeline) {
      records.push({
        _reportType: "dealsByPipeline",
        pipeline_name: data.pipeline_name,
        deals_count: data.deals_count,
        total_amount: Math.round(data.total_amount * 100) / 100,
      });
    }

    // Story 25.3: Deals by owner records (AC #3, unassigned excluded)
    for (const [, data] of dealsByOwner) {
      records.push({
        _reportType: "dealsByOwner",
        owner_name: data.owner_name,
        deals_count: data.deals_count,
        total_amount: Math.round(data.total_amount * 100) / 100,
      });
    }

    // Story 25.3: Deals by type records (AC #4)
    for (const [, data] of dealsByType) {
      records.push({
        _reportType: "dealsByType",
        deal_type: data.deal_type,
        deals_count: data.deals_count,
      });
    }

    // Story 25.5: Contacts by source detail records (AC #2)
    // Sort descending by contacts_count
    const sourceDetailEntries = [...contactsBySourceDetail.values()]
      .sort((a, b) => b.count - a.count);
    for (const entry of sourceDetailEntries) {
      records.push({
        _reportType: "contactsBySourceDetail",
        source: entry.source,
        drilldown1: entry.drilldown1,
        source_detail: `${entry.source} > ${entry.drilldown1}`, // composite for display
        contacts_count: entry.count,
      });
    }

    // Story 25.5: Contacts by latest source records (AC #3)
    const latestSourceEntries = [...contactsByLatestSource.entries()]
      .sort((a, b) => b[1] - a[1]);
    for (const [lsSource, lsCount] of latestSourceEntries) {
      records.push({
        _reportType: "contactsByLatestSource",
        source: lsSource,
        contacts_count: lsCount,
      });
    }

    // Story 25.5: Daily contacts by source records (AC #8)
    // Top 5 sources + "other" aggregation
    const sourceTotals = new Map<string, number>();
    for (const [key, count] of dailyContactsBySource) {
      const dcsSource = key.split("::")[1]!;
      sourceTotals.set(dcsSource, (sourceTotals.get(dcsSource) ?? 0) + count);
    }
    const topSources = [...sourceTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([s]) => s);
    const topSet = new Set(topSources);

    // Re-aggregate daily counts with top-5 + "other"
    const dailyBySourceAgg = new Map<string, number>();
    for (const [key, count] of dailyContactsBySource) {
      const [dayKey, dcsSource] = key.split("::");
      const bucket = topSet.has(dcsSource!) ? dcsSource! : "other";
      const aggKey = `${dayKey}::${bucket}`;
      dailyBySourceAgg.set(aggKey, (dailyBySourceAgg.get(aggKey) ?? 0) + count);
    }

    // Generate records sorted by date then source
    const dailyBySourceRecords = [...dailyBySourceAgg.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, count]) => {
        const [date, dcsSource] = key.split("::");
        return {
          _reportType: "dailyContactsBySource" as const,
          date: date!,
          source: dcsSource!,
          contacts_count: count,
        };
      });
    records.push(...dailyBySourceRecords);

    // ========================================================================
    // Story 25.6: Email Statistics (graceful skip if `content` scope missing)
    // ========================================================================
    const emailScopeCacheKey = `hubspot:email-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    // R1-H2: Parallelize both scope-cache lookups — the email and traffic
    // scopes are independent cache entries and a portal may have one without
    // the other. Also declare the traffic cache key here so both KV.get calls
    // share a single round-trip cost.
    const trafficScopeCacheKey = `hubspot:traffic-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    const [scopeCached, trafficScopeCached] = await Promise.all([
      kv.get(emailScopeCacheKey),
      kv.get(trafficScopeCacheKey),
    ]);

    // Initialize summary record with zero-value email fields so downstream
    // widgets always find the keys — removes need for null-guards everywhere.
    const summaryRecord = records.find(
      (r) => (r as Record<string, unknown>)._reportType === "summary",
    ) as Record<string, unknown> | undefined;

    if (summaryRecord) {
      for (const counter of EMAIL_COUNTERS) summaryRecord[`email_${counter}`] = 0;
      for (const [, targetKey] of EMAIL_RATIO_KEYS) summaryRecord[targetKey] = 0;
      summaryRecord.email_open_computer = 0;
      summaryRecord.email_open_mobile = 0;
      summaryRecord.email_open_unknown = 0;
      // M4: email_stats_available is metadata, NOT a widget-bindable metric.
      // Widgets should inspect this flag to decide whether to show a "connect content scope" CTA.
      summaryRecord.email_stats_available = scopeCached !== "true";
    }

    if (scopeCached !== "true") {
      // R2-H3: Email Events v1 returns only last 30 days of events. If the requested
      // date range is older than 30 days, truncate the start to 30 days ago so we don't
      // silently drop pre-period events.
      const EMAIL_EVENTS_HISTORY_MS = 30 * 86400000;
      const eventsStart = new Date(
        Math.max(dateRange.start.getTime(), Date.now() - EMAIL_EVENTS_HISTORY_MS),
      );

      // R2-H2: Two-phase fetch to reduce peak burst pressure on HubSpot's 100 req/10s
      // budget. Phase 1 = stats + emails (2 concurrent). If either returns 403 we skip
      // Phase 2 entirely, avoiding 8 wasted event calls when the scope is missing.
      const [statsResult, emailsResult] = await Promise.all([
        fetchEmailStatistics(accessToken, dateRange.start, dateRange.end),
        fetchMarketingEmails(accessToken, EMAIL_CAMPAIGNS_PAGE_SIZE),
      ]);

      if (!statsResult.success) return statsResult;
      if (!emailsResult.success) return emailsResult;

      const phase1ScopeMissing =
        statsResult.data.kind === "scope_missing" ||
        emailsResult.data.kind === "scope_missing";

      if (phase1ScopeMissing) {
        await kv.put(emailScopeCacheKey, "true", {
          expirationTtl: EMAIL_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.email_stats_available = false;
      } else {
        // Phase 2: fetch all 8 event types in parallel (8 req, within 10% of burst budget).
        const eventResults = await Promise.all(
          EMAIL_EVENT_TYPES.map((eventType) =>
            fetchEmailEvents(accessToken, eventType, eventsStart, dateRange.end),
          ),
        );

        for (const result of eventResults) {
          if (!result.success) return result;
        }

        const phase2ScopeMissing = eventResults.some(
          (r) => r.success && r.data.kind === "scope_missing",
        );

        if (phase2ScopeMissing) {
          await kv.put(emailScopeCacheKey, "true", {
            expirationTtl: EMAIL_SCOPE_CACHE_TTL_SECONDS,
          });
          if (summaryRecord) summaryRecord.email_stats_available = false;
        } else {
          // All endpoints returned data — commit atomically.
          if (statsResult.data.kind === "ok" && summaryRecord) {
            const { summary: emailSummary, deviceRecords } = processEmailAggregate(
              statsResult.data.data.aggregate,
            );
            Object.assign(summaryRecord, emailSummary);
            records.push(...deviceRecords);
          }

          if (emailsResult.data.kind === "ok") {
            const campaignRecords = buildEmailCampaignRecords(emailsResult.data.data.emails);
            records.push(...campaignRecords);
            if (summaryRecord && emailsResult.data.data.truncated) {
              summaryRecord.email_campaigns_truncated = true;
            }
          }

          // R2-H3: If requested start < 30d ago, flag events as partial
          if (summaryRecord && dateRange.start.getTime() < eventsStart.getTime()) {
            summaryRecord.email_events_window_truncated = true;
          }

          const eventsByType = new Map<EmailEventType, { created: number }[]>();
          for (let i = 0; i < EMAIL_EVENT_TYPES.length; i++) {
            const result = eventResults[i]!;
            const eventType = EMAIL_EVENT_TYPES[i]!;
            if (!result.success) continue;
            if (result.data.kind === "ok") {
              eventsByType.set(
                eventType,
                result.data.data.map((e) => ({ created: e.created })),
              );
            }
          }
          const dailyEventRecords = buildDailyEmailEventRecords(eventsByType);
          records.push(...dailyEventRecords);
        }
      }
    }

    // ========================================================================
    // Story 25.7: Traffic Analytics (graceful skip if `content` scope missing)
    //
    // 3 parallel Analytics v2 calls. Cached scope-missing flag is distinct from
    // email's — a portal may have Analytics access but not email stats, or vice
    // versa. We never assume they fail together. Cache key + lookup already
    // happened in parallel with the email scope check above (R1-H2).
    // ========================================================================
    if (summaryRecord) {
      // Initialize zero-value traffic fields so widgets always find the keys.
      Object.assign(summaryRecord, TRAFFIC_ZERO_SUMMARY);
      summaryRecord.traffic_stats_available = trafficScopeCached !== "true";
    }

    if (trafficScopeCached !== "true") {
      const [totalsResult, sourcesResult, sessionsDailyResult] = await Promise.all([
        fetchTrafficTotals(accessToken, dateRange.start, dateRange.end),
        fetchTrafficSources(accessToken, dateRange.start, dateRange.end),
        fetchTrafficSessionsDaily(accessToken, dateRange.start, dateRange.end),
      ]);

      if (!totalsResult.success) return totalsResult;
      if (!sourcesResult.success) return sourcesResult;
      if (!sessionsDailyResult.success) return sessionsDailyResult;

      const anyScopeMissing =
        totalsResult.data.kind === "scope_missing" ||
        sourcesResult.data.kind === "scope_missing" ||
        sessionsDailyResult.data.kind === "scope_missing";

      if (anyScopeMissing) {
        await kv.put(trafficScopeCacheKey, "true", {
          expirationTtl: TRAFFIC_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.traffic_stats_available = false;
      } else {
        // All 3 endpoints returned data — commit atomically to avoid partial state.
        if (totalsResult.data.kind === "ok" && summaryRecord) {
          Object.assign(summaryRecord, processTrafficTotals(totalsResult.data.data));
        }

        if (sourcesResult.data.kind === "ok") {
          const { records: sourceRecords, bucketSessions } = processTrafficSources(
            sourcesResult.data.data,
          );
          records.push(...sourceRecords);
          if (summaryRecord) {
            for (const bucket of Object.keys(bucketSessions) as TrafficSourceBucket[]) {
              const key = TRAFFIC_SOURCE_SUMMARY_KEYS[bucket];
              if (key) summaryRecord[key] = bucketSessions[bucket];
            }
          }
        }

        if (sessionsDailyResult.data.kind === "ok") {
          const dailyRecords = processTrafficSessionsDaily(sessionsDailyResult.data.data);
          records.push(...dailyRecords);
        }
      }
    }

    // ========================================================================
    // Story 25.8: Marketing Campaigns (graceful skip if `content` scope missing)
    //
    // Two endpoints per campaign: /reports/metrics (4 fields) and /reports/revenue
    // (5 fields). With MAX_CAMPAIGNS=50 and CAMPAIGN_BATCH_SIZE=5, worst case is
    // 1 list call + 20 sequential batches of 10 concurrent report calls =
    // ~101 total API calls per fetch cycle. Batches are paced with
    // CAMPAIGN_BATCH_DELAY_MS between them to respect the 100 req/10s burst limit.
    // ========================================================================
    const campaignScopeCacheKey = `hubspot:campaigns-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    const campaignScopeCached = await kv.get(campaignScopeCacheKey);

    if (summaryRecord) {
      Object.assign(summaryRecord, CAMPAIGN_ZERO_SUMMARY);
      summaryRecord.campaign_currency_code = "";
      summaryRecord.campaign_currency_mixed = false;
      summaryRecord.campaigns_count = 0;
      summaryRecord.campaigns_available = campaignScopeCached !== "true";
    }

    if (campaignScopeCached !== "true") {
      const campaignsResult = await fetchCampaigns(accessToken);
      if (!campaignsResult.success) return campaignsResult;

      if (campaignsResult.data.kind === "scope_missing") {
        await kv.put(campaignScopeCacheKey, "true", {
          expirationTtl: CAMPAIGN_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.campaigns_available = false;
      } else {
        const { campaigns, truncated: campaignsTruncated } = campaignsResult.data.data;

        if (campaigns.length > 0) {
          // Fetch both reports per campaign with rate-limit pacing.
          // Each batch fires 2 × CAMPAIGN_BATCH_SIZE concurrent requests
          // (one metrics + one revenue call per sliced campaign).
          const reports: Array<{
            campaign: HubSpotCampaign;
            metrics: CampaignMetricsReport;
            revenue: CampaignRevenueReport;
          }> = [];
          let scopeDroppedMidway = false;

          for (let i = 0; i < campaigns.length; i += CAMPAIGN_BATCH_SIZE) {
            const slice = campaigns.slice(i, i + CAMPAIGN_BATCH_SIZE);
            // Run metrics + revenue in parallel per campaign. Keep their result
            // types separate so TS can narrow `.data.data` on the `ok` branch.
            const [metricsResults, revenueResults] = await Promise.all([
              Promise.all(
                slice.map((c) =>
                  fetchCampaignMetrics(accessToken, c.id, dateRange.start, dateRange.end),
                ),
              ),
              Promise.all(
                slice.map((c) =>
                  fetchCampaignRevenue(accessToken, c.id, dateRange.start, dateRange.end),
                ),
              ),
            ]);

            for (let j = 0; j < slice.length; j++) {
              const campaign = slice[j]!;
              const metricsRes = metricsResults[j]!;
              const revenueRes = revenueResults[j]!;

              if (!metricsRes.success) return metricsRes;
              if (!revenueRes.success) return revenueRes;

              // If scope is dropped mid-run (rare: admin revokes during fetch),
              // bail out of this block atomically — don't commit partial data.
              if (
                metricsRes.data.kind === "scope_missing" ||
                revenueRes.data.kind === "scope_missing"
              ) {
                scopeDroppedMidway = true;
                break;
              }

              reports.push({
                campaign,
                metrics: metricsRes.data.data,
                revenue: revenueRes.data.data,
              });
            }

            if (scopeDroppedMidway) break;

            // Pace between batches (skip after the final batch)
            if (i + CAMPAIGN_BATCH_SIZE < campaigns.length) {
              await campaignSleep(CAMPAIGN_BATCH_DELAY_MS);
            }
          }

          if (scopeDroppedMidway) {
            await kv.put(campaignScopeCacheKey, "true", {
              expirationTtl: CAMPAIGN_SCOPE_CACHE_TTL_SECONDS,
            });
            if (summaryRecord) summaryRecord.campaigns_available = false;
          } else {
            const { records: campaignRecords, summaryDelta } = processCampaigns(
              campaigns,
              reports,
            );
            records.push(...campaignRecords);
            if (summaryRecord) {
              Object.assign(summaryRecord, summaryDelta);
              if (campaignsTruncated) {
                summaryRecord.campaigns_truncated = true;
              }
            }
          }
        }
      }
    }

    // ========================================================================
    // Story 25.9: Companies, Tickets & Engagements
    //
    // Companies use the long-standing `crm.objects.companies.read` scope, so
    // we always attempt the fetch. Tickets + engagements require the new
    // `tickets` scope; pre-25.9 connections will 403 and we degrade gracefully
    // (zero summary fields, `crm_scope_available = false` flag). Results are
    // cached in KV for 24h to avoid re-probing on every fetch cycle.
    //
    // Engagements (calls/meetings/notes/tasks) are fetched via search by
    // createdate so they match the dashboard's date range. Tasks are further
    // filtered to COMPLETED per AC #3 directive #111.
    // ========================================================================
    const crmScopeCacheKey = `hubspot:crm-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    const crmScopeCached = await kv.get(crmScopeCacheKey);

    if (summaryRecord) {
      Object.assign(summaryRecord, CRM_ZERO_SUMMARY);
      summaryRecord.crm_scope_available = crmScopeCached !== "true";
    }

    // Companies fetch always runs — companies scope was present before 25.9.
    const companiesResult = await fetchCrmObjectsWithScope(
      accessToken,
      "companies",
      [...COMPANY_PROPERTIES],
    );
    if (!companiesResult.success) return companiesResult;

    if (companiesResult.data.kind === "ok") {
      const { objects: companies, truncated: companiesTruncated } = companiesResult.data.data;
      const { records: companyRecords, summaryDelta: companyDelta } = processCompanies(
        companies,
        dateRange.start,
        dateRange.end,
      );
      records.push(...companyRecords);
      if (summaryRecord) {
        Object.assign(summaryRecord, companyDelta);
        if (companiesTruncated) summaryRecord.companies_truncated = true;
      }
    }
    // If scope_missing on companies (very unusual) we leave zeros in place.

    // Tickets + engagements: skip when scope already flagged missing in KV.
    if (crmScopeCached !== "true") {
      // Engagement search only needs object existence (for counts), so we request
      // the minimal property set. For tasks we additionally need `hs_task_status`
      // to filter to COMPLETED per AC #3. HubSpot v3 requires at least one
      // property in the search body — `createdate` is present on all CRM v3
      // objects and cheaper to fetch than larger properties.
      //
      // R2-M1: `tickets_new` is computed in-memory from the full list's
      // createdate property — no second search call needed. Saves 1 request
      // of our 4-req/s Search API budget per fetch cycle.
      const [
        ticketsAllResult,
        callsResult,
        meetingsResult,
        emailsResult,
        notesResult,
        tasksResult,
      ] = await Promise.all([
        fetchCrmObjectsWithScope(accessToken, "tickets", [...TICKET_PROPERTIES]),
        searchCrmObjectsWithScope(
          accessToken,
          "calls",
          ["createdate"],
          "createdate",
          dateRange.start,
          dateRange.end,
        ),
        searchCrmObjectsWithScope(
          accessToken,
          "meetings",
          ["createdate"],
          "createdate",
          dateRange.start,
          dateRange.end,
        ),
        searchCrmObjectsWithScope(
          accessToken,
          "emails",
          ["createdate"],
          "createdate",
          dateRange.start,
          dateRange.end,
        ),
        searchCrmObjectsWithScope(
          accessToken,
          "notes",
          ["createdate"],
          "createdate",
          dateRange.start,
          dateRange.end,
        ),
        searchCrmObjectsWithScope(
          accessToken,
          "tasks",
          ["hs_task_status"],
          "createdate",
          dateRange.start,
          dateRange.end,
        ),
      ]);

      // Bail out on hard failures first. scope_missing is not a failure.
      const allResults = [
        ticketsAllResult,
        callsResult,
        meetingsResult,
        emailsResult,
        notesResult,
        tasksResult,
      ];
      for (const r of allResults) {
        if (!r.success) return r;
      }

      const anyScopeMissing = allResults.some(
        (r) => r.success && r.data.kind === "scope_missing",
      );

      if (anyScopeMissing) {
        await kv.put(crmScopeCacheKey, "true", {
          expirationTtl: CRM_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.crm_scope_available = false;
      } else {
        // All ok — commit atomically. Clear any stale scope-missing flag so
        // re-authorized portals re-enable tickets immediately (R1-M1 fix).
        if (crmScopeCached === "true") {
          await kv.delete(crmScopeCacheKey);
        }
        const ticketsAllData =
          ticketsAllResult.success && ticketsAllResult.data.kind === "ok"
            ? ticketsAllResult.data.data
            : { objects: [] as HubSpotCrmObject[], truncated: false };

        const { records: ticketRecords, summaryDelta: ticketDelta } = processTickets(
          ticketsAllData.objects,
          dateRange.start,
          dateRange.end,
        );
        records.push(...ticketRecords);
        if (summaryRecord) {
          Object.assign(summaryRecord, ticketDelta);
          if (ticketsAllData.truncated) {
            summaryRecord.tickets_truncated = true;
          }
        }

        // Engagement summaries — just counts. Emails here = CRM-tracked emails
        // (separate from Marketing Email stats already covered by Story 25.6).
        const extractObjects = (
          r: typeof ticketsAllResult,
        ): HubSpotCrmObject[] =>
          r.success && r.data.kind === "ok" ? r.data.data.objects : [];

        const calls = extractObjects(callsResult);
        const meetings = extractObjects(meetingsResult);
        const crmEmails = extractObjects(emailsResult);
        const notes = extractObjects(notesResult);
        const tasks = extractObjects(tasksResult);

        let tasksCompleted = 0;
        for (const task of tasks) {
          const status = (task.properties?.hs_task_status ?? "").trim().toUpperCase();
          if (status === TASK_COMPLETED_STATUS) tasksCompleted++;
        }

        if (summaryRecord) {
          summaryRecord.total_calls = calls.length;
          summaryRecord.total_meetings = meetings.length;
          summaryRecord.total_emails_crm = crmEmails.length;
          summaryRecord.total_notes = notes.length;
          summaryRecord.total_tasks_completed = tasksCompleted;
        }
      }
    }

    // ========================================================================
    // Story 25.10: Forms & Marketing Events (graceful skip if optional scopes missing)
    //
    // Forms path: 1 list call + N per-form submission-count calls (N capped at
    // MAX_FORMS=50). Events path: 1 list call — counters are embedded on the
    // response, no per-event calls needed.
    //
    // Both scope flags cached independently (a portal may grant forms but not
    // marketing_events, or vice versa). 403 → scope_missing → zeros + flag.
    // ========================================================================
    const formsScopeCacheKey = `hubspot:forms-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    const eventsScopeCacheKey = `hubspot:events-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    const [formsScopeCached, eventsScopeCached] = await Promise.all([
      kv.get(formsScopeCacheKey),
      kv.get(eventsScopeCacheKey),
    ]);

    if (summaryRecord) {
      Object.assign(summaryRecord, FORMS_ZERO_SUMMARY);
      Object.assign(summaryRecord, EVENTS_ZERO_SUMMARY);
      summaryRecord.forms_count = 0;
      summaryRecord.events_count = 0;
      summaryRecord.forms_available = formsScopeCached !== "true";
      summaryRecord.events_available = eventsScopeCached !== "true";
    }

    if (formsScopeCached !== "true") {
      const formsResult = await fetchForms(accessToken);
      if (!formsResult.success) return formsResult;

      if (formsResult.data.kind === "scope_missing") {
        await kv.put(formsScopeCacheKey, "true", {
          expirationTtl: FORMS_EVENTS_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.forms_available = false;
      } else {
        const { forms, truncated: formsTruncated } = formsResult.data.data;

        if (forms.length > 0) {
          // Fetch per-form submission counts in batched parallel calls
          const submissionCounts: Array<{ formGuid: string; count: number; truncated: boolean }> = [];
          let formsScopeDroppedMidway = false;

          for (let i = 0; i < forms.length; i += FORM_SUBMISSION_BATCH_SIZE) {
            const slice = forms.slice(i, i + FORM_SUBMISSION_BATCH_SIZE);
            const results = await Promise.all(
              slice.map((f) => fetchFormSubmissionsCount(accessToken, f.id)),
            );

            for (let j = 0; j < slice.length; j++) {
              const form = slice[j]!;
              const res = results[j]!;
              if (!res.success) return res;
              if (res.data.kind === "scope_missing") {
                formsScopeDroppedMidway = true;
                break;
              }
              submissionCounts.push({
                formGuid: form.id,
                count: res.data.data.count,
                truncated: res.data.data.truncated,
              });
            }

            if (formsScopeDroppedMidway) break;

            // Pace between batches (skip after the final batch)
            if (i + FORM_SUBMISSION_BATCH_SIZE < forms.length) {
              await formSubmissionSleep(FORM_SUBMISSION_BATCH_DELAY_MS);
            }
          }

          if (formsScopeDroppedMidway) {
            await kv.put(formsScopeCacheKey, "true", {
              expirationTtl: FORMS_EVENTS_SCOPE_CACHE_TTL_SECONDS,
            });
            if (summaryRecord) summaryRecord.forms_available = false;
          } else {
            // R1-H2: formsScopeCached is known to be !== "true" for this whole block
            // (gated above), so there's never a stale flag to clear here. The only
            // re-auth recovery path we need is via KV TTL (24h) or when scope_missing
            // returns — which is handled in the outer `if (formsResult.data.kind === "scope_missing")`
            // branch by re-writing the flag (a no-op if already set).
            const { records: formRecords, summaryDelta: formsDelta } = processForms(
              forms,
              submissionCounts,
            );
            records.push(...formRecords);
            if (summaryRecord) {
              Object.assign(summaryRecord, formsDelta);
              if (formsTruncated) summaryRecord.forms_truncated = true;
            }
          }
        }
      }
    }

    if (eventsScopeCached !== "true") {
      const eventsResult = await fetchMarketingEvents(accessToken);
      if (!eventsResult.success) return eventsResult;

      if (eventsResult.data.kind === "scope_missing") {
        await kv.put(eventsScopeCacheKey, "true", {
          expirationTtl: FORMS_EVENTS_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.events_available = false;
      } else {
        const { events, truncated: eventsTruncated } = eventsResult.data.data;
        // R1-H2: Dead branch removed. This whole block is gated by
        // `eventsScopeCached !== "true"`, so the cache is already not set.
        // TTL-based re-enable (24h) is the recovery path.
        // R2-M3: Filter events by startDateTime within the requested period so
        // historical events don't clutter active-period dashboards.
        const { records: eventRecords, summaryDelta: eventsDelta } = processMarketingEvents(
          events,
          dateRange,
        );
        records.push(...eventRecords);
        if (summaryRecord) {
          Object.assign(summaryRecord, eventsDelta);
          if (eventsTruncated) summaryRecord.events_truncated = true;
        }
      }
    }

    // ========================================================================
    // Story 25.11 (Task 3): Remaining CRM contact metrics + geography dims.
    //
    // Derived from the existing `contacts` list (Story 25.5 search response
    // already widened to include `email`, `days_to_close`, `country`, `state`,
    // `city` above). Zero additional API calls. Always runs — these metrics
    // piggyback on the `crm.objects.contacts.read` scope that's required for
    // core CRM functionality.
    // ========================================================================
    {
      const { emailDomainRecords, geographyRecords, summaryDelta: contactsDelta } =
        processContactExtras(contacts);
      records.push(...emailDomainRecords);
      records.push(...geographyRecords);
      if (summaryRecord) Object.assign(summaryRecord, contactsDelta);
    }

    // ========================================================================
    // Story 25.11 (Task 4): Deal forecast probability per deal.
    //
    // Built from the merged deal set (Story 25.3). Skipped records (probability
    // missing / invalid) do not emit a `dealForecast` row. Summary aggregates
    // already include `avg_deal_amount` etc. via existing code paths.
    // ========================================================================
    {
      const forecastRecords = processDealForecast(deals, stageMap);
      records.push(...forecastRecords);
    }

    // ========================================================================
    // Story 25.11 (Task 1): Workflows (Automation v4 BETA).
    //
    // 1 list call + up to MAX_WORKFLOWS details calls (batched 5-parallel with
    // rate-limit pacing). Scope cached 24h on missing/revoked. BETA API —
    // any parse failure degrades gracefully to zero summary values.
    // ========================================================================
    const workflowsScopeCacheKey = `hubspot:workflows-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    const workflowsScopeCached = await kv.get(workflowsScopeCacheKey);

    if (summaryRecord) {
      Object.assign(summaryRecord, WORKFLOWS_ZERO_SUMMARY);
      summaryRecord.workflows_count = 0;
      summaryRecord.workflows_available = workflowsScopeCached !== "true";
    }

    if (workflowsScopeCached !== "true") {
      const workflowsResult = await fetchWorkflows(accessToken);
      if (!workflowsResult.success) return workflowsResult;

      if (workflowsResult.data.kind === "scope_missing") {
        await kv.put(workflowsScopeCacheKey, "true", {
          expirationTtl: WORKFLOWS_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.workflows_available = false;
      } else {
        const { workflows, truncated: workflowsTruncated } = workflowsResult.data.data;

        if (workflows.length > 0) {
          const statsByFlowId = new Map<string, HubSpotWorkflowStats>();
          let workflowScopeDroppedMidway = false;

          for (let i = 0; i < workflows.length; i += WORKFLOW_BATCH_SIZE) {
            const slice = workflows.slice(i, i + WORKFLOW_BATCH_SIZE);
            const detailsResults = await Promise.all(
              slice.map((wf) => fetchWorkflowDetails(accessToken, wf.id)),
            );

            for (let j = 0; j < slice.length; j++) {
              const wf = slice[j]!;
              const res = detailsResults[j]!;
              if (!res.success) return res;
              if (res.data.kind === "scope_missing") {
                workflowScopeDroppedMidway = true;
                break;
              }
              if (res.data.data != null) {
                statsByFlowId.set(wf.id, res.data.data);
              }
            }
            if (workflowScopeDroppedMidway) break;

            if (i + WORKFLOW_BATCH_SIZE < workflows.length) {
              await hubspotBatchSleep(WORKFLOW_BATCH_DELAY_MS);
            }
          }

          if (workflowScopeDroppedMidway) {
            await kv.put(workflowsScopeCacheKey, "true", {
              expirationTtl: WORKFLOWS_SCOPE_CACHE_TTL_SECONDS,
            });
            if (summaryRecord) summaryRecord.workflows_available = false;
          } else {
            const { records: workflowRecords, summaryDelta: workflowsDelta } =
              processWorkflows(workflows, statsByFlowId);
            records.push(...workflowRecords);
            if (summaryRecord) {
              Object.assign(summaryRecord, workflowsDelta);
              summaryRecord.workflows_count = workflows.length;
              if (workflowsTruncated) summaryRecord.workflows_truncated = true;
            }
          }
        }
      }
    }

    // ========================================================================
    // Story 25.11 (Task 2): Custom Behavioral Events.
    //
    // 1 list call for event definitions. If the portal has zero custom events
    // (most portals), short-circuit without any per-type calls. Otherwise
    // batch 5-parallel per-type count calls with rate-limit pacing.
    // ========================================================================
    const customEventsScopeCacheKey = `hubspot:custom-events-scope-missing:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
    const customEventsScopeCached = await kv.get(customEventsScopeCacheKey);

    if (summaryRecord) {
      Object.assign(summaryRecord, CUSTOM_EVENTS_ZERO_SUMMARY);
      summaryRecord.custom_event_types_count = 0;
      summaryRecord.custom_events_available = customEventsScopeCached !== "true";
    }

    if (customEventsScopeCached !== "true") {
      const defsResult = await fetchEventDefinitions(accessToken);
      if (!defsResult.success) return defsResult;

      if (defsResult.data.kind === "scope_missing") {
        await kv.put(customEventsScopeCacheKey, "true", {
          expirationTtl: CUSTOM_EVENTS_SCOPE_CACHE_TTL_SECONDS,
        });
        if (summaryRecord) summaryRecord.custom_events_available = false;
      } else {
        const definitions = defsResult.data.data;
        if (definitions.length > 0) {
          // Cap iterations to keep per-cycle call budget predictable.
          const scoped = definitions.slice(0, MAX_EVENT_TYPES);
          const truncatedDefs = definitions.length > MAX_EVENT_TYPES;
          const perTypeResults: Array<{
            name: string;
            label: string;
            count: number;
            latestOccurredAt: string | null;
            truncated: boolean;
          }> = [];
          let customEventsScopeDroppedMidway = false;

          for (let i = 0; i < scoped.length; i += EVENT_TYPE_BATCH_SIZE) {
            const slice = scoped.slice(i, i + EVENT_TYPE_BATCH_SIZE);
            const results = await Promise.all(
              slice.map((def) =>
                fetchCustomEventCount(accessToken, def.name, dateRange.start, dateRange.end),
              ),
            );

            for (let j = 0; j < slice.length; j++) {
              const def = slice[j]!;
              const res = results[j]!;
              if (!res.success) return res;
              if (res.data.kind === "scope_missing") {
                customEventsScopeDroppedMidway = true;
                break;
              }
              perTypeResults.push({
                name: def.name,
                label: def.label?.trim() || def.name,
                count: res.data.data.count,
                latestOccurredAt: res.data.data.latestOccurredAt,
                truncated: res.data.data.truncated,
              });
            }
            if (customEventsScopeDroppedMidway) break;

            if (i + EVENT_TYPE_BATCH_SIZE < scoped.length) {
              await hubspotBatchSleep(EVENT_TYPE_BATCH_DELAY_MS);
            }
          }

          if (customEventsScopeDroppedMidway) {
            await kv.put(customEventsScopeCacheKey, "true", {
              expirationTtl: CUSTOM_EVENTS_SCOPE_CACHE_TTL_SECONDS,
            });
            if (summaryRecord) summaryRecord.custom_events_available = false;
          } else {
            let totalCustomEventCount = 0;
            let anyTypeTruncated = false;
            // Pre-sort perTypeResults descending by count so data-table widgets
            // land the top events at the top without requiring a client sort.
            // Build records in that order and append once — keeps the overall
            // `records` array stable (no global re-sort that might reorder
            // summary/dailyOverview/other reportType entries).
            const sortedResults = [...perTypeResults].sort(
              (a, b) => b.count - a.count || a.name.localeCompare(b.name),
            );
            for (const entry of sortedResults) {
              totalCustomEventCount += entry.count;
              if (entry.truncated) anyTypeTruncated = true;
              records.push({
                _reportType: "customEvents",
                event_type: entry.name,
                event_label: entry.label,
                event_count: entry.count,
                custom_event_count: entry.count,
                latest_timestamp: entry.latestOccurredAt,
                custom_event_latest_timestamp: entry.latestOccurredAt,
              });
            }
            if (summaryRecord) {
              summaryRecord.custom_event_count = totalCustomEventCount;
              summaryRecord.custom_event_types_count = perTypeResults.length;
              if (anyTypeTruncated) {
                summaryRecord.custom_events_truncated = true;
              }
              if (truncatedDefs) {
                summaryRecord.custom_event_types_truncated = true;
              }
            }
          }
        }
      }
    }

    // Keep summary recordCount accurate (records mutated above)
    return adapterOk({
      platform: "hubspot",
      accountId: accountId ?? "",
      dateRange,
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<boolean>> {
    try {
      const tokenInfo = await verifyHubSpotAccessToken(credentials.accessToken);
      if (!tokenInfo.hub_id) {
        return adapterErr({
          code: "token_invalid",
          message: "HubSpot token verification returned no hub_id",
          retryable: false,
        });
      }
      return adapterOk(true);
    } catch (err) {
      // M3: Distinguish network errors (retryable) from token errors (permanent)
      const message = (err as Error).message ?? "";
      const isNetworkError =
        message.includes("fetch failed") ||
        message.includes("network") ||
        message.includes("timeout") ||
        message.includes("abort");
      return adapterErr({
        code: isNetworkError ? "network_error" : "token_expired",
        message: sanitizeErrorMessage(
          `HubSpot connection validation failed: ${message}`,
        ),
        retryable: isNetworkError,
      });
    }
  },

  async refreshToken(
    _ctx: AdapterRefreshContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_invalid",
        message: "No refresh token available for HubSpot",
        retryable: false,
      });
    }

    const env = _ctx.env as unknown as Record<string, string>;
    const clientId = env.HUBSPOT_CLIENT_ID || "";
    const clientSecret = env.HUBSPOT_CLIENT_SECRET || "";

    if (!clientId || !clientSecret) {
      return adapterErr({
        code: "token_invalid",
        message:
          "Missing HUBSPOT_CLIENT_ID or HUBSPOT_CLIENT_SECRET env bindings",
        retryable: false,
      });
    }

    try {
      const tokenResponse = await refreshHubSpotToken(
        credentials.refreshToken,
        clientId,
        clientSecret,
      );
      return adapterOk({
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token,
        expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000),
      });
    } catch (err) {
      return adapterErr({
        code: "token_expired",
        message: sanitizeErrorMessage(
          `HubSpot token refresh failed: ${(err as Error).message}`,
        ),
        retryable: false,
      });
    }
  },

  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<AccountInfo[]>> {
    try {
      const tokenInfo = await verifyHubSpotAccessToken(credentials.accessToken);

      return adapterOk([
        {
          id: String(tokenInfo.hub_id),
          name: tokenInfo.hub_domain || `HubSpot Portal ${tokenInfo.hub_id}`,
          platform: "hubspot" as const,
          // Catalog contract (Epic 31, Story 31.10): ACTUAL granted scopes
          // from the v3 introspect response (granted ≠ requested — optional
          // scopes vary per portal). Array.isArray + per-element string
          // filter guard introspect contract drift: discovery output is the
          // authorization allowlist, so never propagate unvalidated shapes
          // (normalize to [] / drop non-string elements at the source —
          // same pattern as snapchat/api.ts organization_ids).
          scopes: Array.isArray(tokenInfo.scopes)
            ? tokenInfo.scopes.filter(
                (s): s is string => typeof s === "string",
              )
            : [],
          metadata: {
            hubId: tokenInfo.hub_id,
            hubDomain: tokenInfo.hub_domain,
            userId: tokenInfo.user_id,
            user: tokenInfo.user,
            appId: tokenInfo.app_id,
          },
        },
      ]);
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to discover HubSpot account: ${(err as Error).message}`,
        ),
        retryable: true,
      });
    }
  },
};
